"use strict";

/**
 * SharpAPI → arbitrage rows for Analysis UI (ArbitragePage).
 * GET /opportunities/arbitrage — see https://docs.sharpapi.io/en/api-reference/opportunities-arbitrage
 */

function toNum(v, fb = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

function toStr(v, fb = "") {
  if (v == null) return fb;
  const s = String(v).trim();
  return s ? s : fb;
}

function toBool(v) {
  return v === true;
}

function parseTsMs(v) {
  if (v == null || v === "") return undefined;
  const ms = Date.parse(String(v));
  return Number.isFinite(ms) ? ms : undefined;
}

function normToken(v) {
  return String(v ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function normBook(v) {
  return String(v ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

/** Conservative bookmaker key for tiering (no external deps). */
function normalizeBookmakerNameForTier(name) {
  let s = normBook(name);
  if (!s) return "";
  s = s.replace(/[^a-z0-9]/g, "");
  if (/^bet365/.test(s) || s === "bet365com" || s === "bet365") return "bet365";
  if (/^pinnacle/.test(s) || s === "pinnaclesports") return "pinnacle";
  if (/^betfair/.test(s) || s === "betfairexchange" || s === "betfair") return "betfair";
  if (/^sbobet/.test(s)) return "sbobet";
  if (/^marathon/.test(s) || s === "marathonbet") return "marathon";
  if (/^188bet/.test(s) || s === "188bet") return "188bet";
  if (/^1xbet/.test(s) || s === "1xbet" || s === "one88bet") return "1xbet";
  if (/^dafabet/.test(s)) return "dafabet";
  return s;
}

function bookmakerTierFromNormalizedKey(key) {
  if (!key) return "C";
  const tierA = new Set(["pinnacle", "bet365", "betfair"]);
  const tierB = new Set(["sbobet", "marathon", "188bet", "1xbet", "dafabet"]);
  if (tierA.has(key)) return "A";
  if (tierB.has(key)) return "B";
  return "C";
}

function commercialWarningArrayLooksStale(warnings) {
  if (!Array.isArray(warnings) || !warnings.length) return false;
  for (let i = 0; i < warnings.length; i += 1) {
    const t = String(warnings[i] ?? "");
    if (/stale|latency|delay|outdated|old\s+odds|slow|behind/i.test(t)) return true;
  }
  return false;
}

function rowLooksLikeEsports(row) {
  if (!row || typeof row !== "object") return false;
  const sport = normToken(row.sport || "");
  const league = normToken(row.league || "");
  const match = normToken(row.match || row.eventName || row.event_name || "");
  const blob = `${sport} ${league} ${match}`;
  if (/\besports\b|e-?sports?|counter-?strike|cs2\b|csgo|dota|league\s+of\s+legends|\blol\b|valorant|overwatch|starcraft|rocket\s+league|rainbow\s+six|r6\b|pubg|fortnite|apex\b/i.test(blob)) {
    return true;
  }
  return sport === "esports" || sport.includes("esport");
}

function classifyMarketFamilyQuality(row) {
  const raw = String(row?.market || row?.marketType || row?.market_type || "").trim();
  const mk = normToken(raw);
  if (!mk) return { tier: "medium", fragile: true, label: "unknown" };

  const fragileHints =
    /player|prop\b|props\b|alternate|alt\s|corner|booking|card|shot|threes|strikeout|pitcher|period\s+\d|quarter\s+\d|set\s+\d|map\s+\d|map\s+winner|first\s+to|race\s+to|inning|half\s+time|1st\s+half|2nd\s+half/i;
  if (fragileHints.test(mk)) return { tier: "low", fragile: true, label: "fragile_or_niche" };

  const highHints =
    /moneyline|match\s*winner|winner|1x2|draw\s+no\s+bet|\bdnb\b|total|over\/under|overunder|spread|handicap|asian\s*handicap|run\s*line|puck\s*line|points\s*total|goals\s*total/i;
  if (highHints.test(mk)) return { tier: "high", fragile: false, label: "standard_liquid" };

  return { tier: "medium", fragile: false, label: "other" };
}

/**
 * Computes commercial-quality fields for a normalized arbitrage row.
 * Mutates and returns the same object reference.
 * @param {object} row
 * @returns {object}
 */
function enrichCommercialArbitrageQuality(row) {
  if (!row || typeof row !== "object") return row;

  const picks = Array.isArray(row.picks) ? row.picks : [];
  const warnings = Array.isArray(row.warnings) ? row.warnings : [];
  const possiblyStale = row.possibly_stale === true;
  const oldestAge = toNum(row.oldest_odds_age_seconds, null);
  const isAlt = row.is_alternate_line === true;
  const isPp = row.is_player_prop === true;
  const evAvail = row.ev_available === true;
  const evPct = toNum(row.ev_percentage, null);
  const isEsports = rowLooksLikeEsports(row);

  const tierCounts = { A: 0, B: 0, C: 0 };

  for (let i = 0; i < picks.length; i += 1) {
    const p = picks[i];
    const rawName = String(p?.book || p?.sportsbook || "").trim();
    const nk = normalizeBookmakerNameForTier(rawName);
    const tier = bookmakerTierFromNormalizedKey(nk);
    if (tier === "A") tierCounts.A += 1;
    else if (tier === "B") tierCounts.B += 1;
    else tierCounts.C += 1;
  }

  const topBookCount = tierCounts.A;
  const hasTopBook = topBookCount >= 1;
  const bookTierSummary = `A:${tierCounts.A}|B:${tierCounts.B}|C:${tierCounts.C}`;

  const mq = classifyMarketFamilyQuality(row);
  let marketQualityTier = mq.tier;
  if (mq.fragile) marketQualityTier = "low";

  const commercialFlags = [];
  const commercialRejectReasons = [];

  if (hasTopBook) commercialFlags.push("top_book_present");
  if (tierCounts.A + tierCounts.B >= 2) {
    commercialFlags.push("two_top_books");
  }
  if (picks.length >= 2 && tierCounts.C === picks.length && tierCounts.A === 0 && tierCounts.B === 0) {
    commercialFlags.push("all_books_low_tier");
  }
  if (possiblyStale) commercialFlags.push("possibly_stale");
  if (oldestAge != null && oldestAge > 120) commercialFlags.push("old_odds");
  if (commercialWarningArrayLooksStale(warnings)) commercialFlags.push("warning_stale");
  if (isAlt) commercialFlags.push("alternate_line");
  if (isPp) commercialFlags.push("player_prop");
  if (mq.fragile || mq.tier === "low") commercialFlags.push("fragile_market");
  if (isEsports && !hasTopBook && tierCounts.B < 2) {
    commercialFlags.push("esports_low_confidence");
  }
  if (evAvail === true && evPct != null && evPct > 0) {
    commercialFlags.push("positive_ev_assist");
  }

  let liquidityScore = 52;
  liquidityScore += Math.min(36, tierCounts.A * 18);
  liquidityScore += Math.min(22, tierCounts.B * 11);
  if (tierCounts.A >= 2) liquidityScore += 8;
  else if (tierCounts.A >= 1 && tierCounts.B >= 1) liquidityScore += 5;

  if (picks.length >= 2 && tierCounts.C === picks.length) liquidityScore -= 28;
  if (isAlt) liquidityScore -= 26;
  if (isPp) liquidityScore -= 65;
  if (possiblyStale) liquidityScore -= 32;
  if (commercialWarningArrayLooksStale(warnings)) liquidityScore -= 18;
  if (oldestAge != null && oldestAge > 0) {
    const agePen = Math.min(28, Math.floor(oldestAge / 45));
    liquidityScore -= agePen;
  }
  if (mq.fragile || mq.tier === "low") liquidityScore -= 22;
  else if (mq.tier === "medium") liquidityScore -= 8;

  if (isEsports) {
    if (tierCounts.A >= 1 && liquidityScore > 60) liquidityScore -= 6;
    else liquidityScore -= 20;
  }

  if (evAvail === true && evPct != null && evPct > 0) liquidityScore += Math.min(10, 4 + Math.floor(evPct / 4));

  if (liquidityScore < 0) liquidityScore = 0;
  if (liquidityScore > 100) liquidityScore = 100;

  if (possiblyStale) commercialRejectReasons.push("possibly_stale");
  if (commercialWarningArrayLooksStale(warnings)) commercialRejectReasons.push("stale_warning");
  if (isAlt) commercialRejectReasons.push("alternate_line");
  if (isPp) commercialRejectReasons.push("player_prop");
  if (picks.length < 2) commercialRejectReasons.push("insufficient_legs");
  if (!hasTopBook && tierCounts.B < 2) commercialRejectReasons.push("low_tier_book_support");
  if (liquidityScore < 38) commercialRejectReasons.push("low_liquidity_score");
  if (isEsports && tierCounts.A === 0 && tierCounts.B < 2) commercialRejectReasons.push("esports_weak_books");
  if (mq.fragile && tierCounts.A === 0) commercialRejectReasons.push("fragile_market");

  let isCommercialSafe = true;
  if (isPp || isAlt || possiblyStale || commercialWarningArrayLooksStale(warnings)) isCommercialSafe = false;
  else if (picks.length < 2) isCommercialSafe = false;
  else if (!hasTopBook && tierCounts.B < 2) isCommercialSafe = false;
  else if (liquidityScore < 46) isCommercialSafe = false;
  else if (mq.tier === "low" && tierCounts.A === 0) isCommercialSafe = false;
  else if (isEsports && (liquidityScore < 62 || tierCounts.A === 0)) isCommercialSafe = false;

  row.topBookCount = topBookCount;
  row.hasTopBook = hasTopBook;
  row.bookTierSummary = bookTierSummary;
  row.liquidityScore = liquidityScore;
  row.commercialFlags = commercialFlags;
  row.commercialRejectReasons = commercialRejectReasons;
  row.isCommercialSafe = isCommercialSafe;
  row.marketQualityTier = marketQualityTier;

  return row;
}

function pickLineToken(leg) {
  if (!leg || typeof leg !== "object") return "";
  const cands = [
    leg.verifiedLine,
    leg.line,
    leg.handicap,
    leg.point,
    leg.total,
    leg.overLine,
    leg.underLine,
  ];
  for (let i = 0; i < cands.length; i += 1) {
    const s = String(cands[i] ?? "").trim();
    if (s && s !== "—" && s !== "-") return s;
  }
  return "";
}

/**
 * Sharp `raw.id` can change across polls for the same economic arbitrage opportunity.
 * Lifecycle/dedupe/new-item logic must therefore use a semantic identity derived from the opportunity structure.
 */
function sharpMarketKeyForGrouping(s) {
  return String(s ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

/**
 * Markets where line threshold variants (2.0 vs 2.5 vs 3.0) are the same economic market family.
 */
function marketSupportsBestLineGrouping(marketStr) {
  const k = sharpMarketKeyForGrouping(marketStr);
  if (!k) return false;
  if (k.includes("total") || k.includes("overunder") || k === "ou") return true;
  if (k.includes("spread") || k.includes("handicap") || k.includes("runline") || k.includes("puckline")) return true;
  if (k.includes("asian")) return true;
  return false;
}

/**
 * Winner / moneyline style markets: no line-threshold variants to collapse.
 */
function marketIsWinnerStyleNoLineVariants(marketStr) {
  const k = sharpMarketKeyForGrouping(marketStr);
  if (!k) return true;
  if (k === "moneyline" || k === "1x2" || k === "drawnobet" || k === "dnb" || k === "matchwinner") return true;
  if (k.includes("1x2") && !k.includes("total") && !k.includes("handicap")) return true;
  return false;
}

/**
 * Period / segment context so 1H totals do not collapse into full-game totals.
 */
function extractPeriodContextForBestLine(rawOrRow) {
  const tokens = [];
  const pushTok = (v) => {
    if (v == null || v === "") return;
    const t = normToken(String(v));
    if (t) tokens.push(t);
  };

  const cands = [
    rawOrRow.period,
    rawOrRow.half,
    rawOrRow.quarter,
    rawOrRow.map,
    rawOrRow.set,
    rawOrRow.currentQuarter,
    rawOrRow.currentPeriod,
    rawOrRow.gameNumber,
    rawOrRow.mapNumber,
    rawOrRow.setNumber,
    rawOrRow.currentMap,
    rawOrRow.currentSet,
  ];
  for (let i = 0; i < cands.length; i += 1) pushTok(cands[i]);

  const gs = rawOrRow.game_state && typeof rawOrRow.game_state === "object" ? rawOrRow.game_state : null;
  if (gs) {
    const gc = [
      gs.period,
      gs.half,
      gs.quarter,
      gs.map,
      gs.set,
      gs.currentQuarter,
      gs.currentPeriod,
      gs.gameNumber,
      gs.mapNumber,
      gs.setNumber,
    ];
    for (let i = 0; i < gc.length; i += 1) pushTok(gc[i]);
  }

  const mt = String(rawOrRow.market_type || rawOrRow.marketType || rawOrRow.market || "");
  if (/\b1\s*st\b.*\b(half|hf)\b|first\s+half|\b1h\b/i.test(mt)) tokens.push("ctx:1h");
  if (/\b2\s*nd\b.*\b(half|hf)\b|second\s+half|\b2h\b/i.test(mt)) tokens.push("ctx:2h");
  if (/\bq1\b|1\s*st\s+quarter|first\s+quarter/i.test(mt)) tokens.push("ctx:q1");
  if (/\bq2\b|2\s*nd\s+quarter|second\s+quarter/i.test(mt)) tokens.push("ctx:q2");
  if (/\bq3\b|3\s*rd\s+quarter|third\s+quarter/i.test(mt)) tokens.push("ctx:q3");
  if (/\bq4\b|4\s*th\s+quarter|fourth\s+quarter/i.test(mt)) tokens.push("ctx:q4");
  if (/\bmap\s*\d+|current\s*map/i.test(mt)) tokens.push("ctx:map_ctx");

  const uniq = [...new Set(tokens)];
  uniq.sort((a, b) => a.localeCompare(b));
  return uniq.join("|");
}

/**
 * Totals vs spread-like for best-line outcome normalization (subset of marketSupportsBestLineGrouping).
 */
function bestLineMarketGroupingMode(marketRaw) {
  const k = sharpMarketKeyForGrouping(marketRaw);
  if (!k) return "";
  if (k.includes("total") || k.includes("overunder") || k === "ou") return "totals";
  if (k.includes("spread") || k.includes("handicap") || k.includes("runline") || k.includes("puckline") || k.includes("asian")) {
    return "spread";
  }
  return "";
}

function escapeRegExp(s) {
  return String(s ?? "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function normalizedPhraseRegex(phrase) {
  const t = normToken(String(phrase ?? ""));
  if (!t) return null;
  const parts = t.split(" ").filter(Boolean).map(escapeRegExp);
  if (!parts.length) return null;
  return new RegExp(`\\b${parts.join("\\s+")}\\b`, "i");
}

function detectTotalsTeamContextForBestLineGrouping(rawOrRow, legsForContext) {
  if (!rawOrRow || typeof rawOrRow !== "object") return "";

  const marketRaw = rawOrRow.market_type || rawOrRow.marketType || rawOrRow.market || "";
  const mt = normToken(marketRaw);
  const mtKey = sharpMarketKeyForGrouping(marketRaw);

  const looksLikeTeamTotal =
    /\bteam\s*totals?\b/.test(mt) ||
    (mtKey && (mtKey.includes("teamtotal") || mtKey.includes("teamtotals"))) ||
    /\bteamtotal\b/.test(mt);

  if (!looksLikeTeamTotal) return "game";

  const hasHomeTok = /\bhome\b|\bteam\s*1\b|\bt1\b/.test(mt);
  const hasAwayTok = /\baway\b|\bteam\s*2\b|\bt2\b/.test(mt);
  if (hasHomeTok && !hasAwayTok) return "home_team";
  if (hasAwayTok && !hasHomeTok) return "away_team";

  const legs = Array.isArray(legsForContext) ? legsForContext : [];
  const sideVotes = [];
  for (let i = 0; i < legs.length; i += 1) {
    const l = legs[i];
    const s1 = spreadSideSemanticFromToken(l?.side);
    const s2 = spreadSideSemanticFromToken(l?.selection_type);
    const v = s1 || s2;
    if (v === "home" || v === "away") sideVotes.push(v);
  }
  const uniqVotes = [...new Set(sideVotes)];
  if (uniqVotes.length === 1) return uniqVotes[0] === "home" ? "home_team" : "away_team";
  if (uniqVotes.length > 1) return "";

  const homeTeam = rawOrRow.home_team ?? rawOrRow.homeTeamName ?? "";
  const awayTeam = rawOrRow.away_team ?? rawOrRow.awayTeamName ?? "";
  const homeRe = normalizedPhraseRegex(homeTeam);
  const awayRe = normalizedPhraseRegex(awayTeam);

  const textVotes = [];
  if (homeRe || awayRe) {
    for (let i = 0; i < legs.length; i += 1) {
      const l = legs[i];
      const selRaw = String(l?.selection ?? l?.sel ?? "");
      const sel = normToken(selRaw);
      if (!sel) continue;

      const hasHome = homeRe ? homeRe.test(sel) : false;
      const hasAway = awayRe ? awayRe.test(sel) : false;
      if (hasHome && hasAway) return "";
      if (hasHome) textVotes.push("home");
      else if (hasAway) textVotes.push("away");
    }
  }
  const uniqText = [...new Set(textVotes)];
  if (uniqText.length === 1) return uniqText[0] === "home" ? "home_team" : "away_team";
  if (uniqText.length > 1) return "";

  return "";
}

function normalizeTotalsSelectionForBestLineGrouping(leg) {
  if (!leg || typeof leg !== "object") return "";
  const st = normToken(leg.selection_type);
  const sd = normToken(leg.side);
  const sel = normToken(String(leg.selection ?? leg.sel ?? ""));
  const blob = [st, sd, sel].filter(Boolean).join(" ");

  const hasOver = /\bover\b/.test(blob);
  const hasUnder = /\bunder\b/.test(blob);
  if (hasOver && hasUnder) return "";
  if (hasOver) return "over";
  if (hasUnder) return "under";

  const stRaw = String(leg.selection_type ?? "").trim().toLowerCase();
  if (stRaw === "over" || stRaw === "o") return "over";
  if (stRaw === "under" || stRaw === "u") return "under";

  return "";
}

function stripSpreadLineFromSelectionText(rawSel) {
  let s = String(rawSel ?? "").trim().replace(/\s+/g, " ");
  if (!s) return "";
  for (let guard = 0; guard < 16; guard += 1) {
    const prev = s;
    s = s.replace(/\s*\([-+]?\d+(?:\.\d+)?\)\s*$/, "").trim();
    s = s.replace(/\s*[-+]\s*\d+(?:\.\d+)?\s*$/, "").trim();
    s = s.replace(/\s*\b(?:pk|pick'?em|pickem)\b\s*$/i, "").trim();
    s = s.replace(/^\s*[-+]\s*\d+(?:\.\d+)?(?:\s*\/\s*[-+]\s*\d+(?:\.\d+)?)?\s+/, "").trim();
    s = s.replace(/^\s*\d+(?:\.\d+)?\s*\/\s*[-+]\s*\d+(?:\.\d+)?\s+/, "").trim();
    if (s === prev) break;
  }
  return s.trim();
}

function spreadSideSemanticFromToken(v) {
  const t = normToken(String(v ?? ""));
  if (!t) return "";
  if (t === "home" || t === "h" || t === "team1" || t === "t1" || t === "1") return "home";
  if (t === "away" || t === "a" || t === "team2" || t === "t2" || t === "2") return "away";
  return "";
}

function normalizeSpreadSelectionForBestLineGrouping(leg, homeTeam, awayTeam) {
  if (!leg || typeof leg !== "object") return "";

  const fromSide = spreadSideSemanticFromToken(leg.side) || spreadSideSemanticFromToken(leg.selection_type);
  if (fromSide) return fromSide;

  const homeN = normToken(String(homeTeam ?? ""));
  const awayN = normToken(String(awayTeam ?? ""));
  if (!homeN && !awayN) return "";

  const stripped = stripSpreadLineFromSelectionText(String(leg.selection ?? leg.sel ?? ""));
  if (!stripped) return "";

  const workN = normToken(stripped);
  if (!workN || /^[-+]?\d+(?:\.\d+)?$/.test(workN)) return "";

  const exactH = homeN && workN === homeN;
  const exactA = awayN && workN === awayN;
  if (exactH && !exactA) return "home";
  if (exactA && !exactH) return "away";

  return "";
}

function normalizeSelectionForBestLineGrouping(leg, mode, homeTeam, awayTeam) {
  if (mode === "totals") return normalizeTotalsSelectionForBestLineGrouping(leg);
  if (mode === "spread") return normalizeSpreadSelectionForBestLineGrouping(leg, homeTeam, awayTeam);
  return "";
}

/**
 * Grouping identity for same-market / different-line suppression only.
 * Intentionally omits line thresholds, odds, profit, timestamps, and raw provider ids.
 * Conservative: empty string when the row is not a clear 2-leg line-market arb or context is ambiguous.
 */
function buildArbitrageBestLineGroupKey(rawOrRow) {
  if (!rawOrRow || typeof rawOrRow !== "object") return "";

  const eventId = normToken(rawOrRow.event_id ?? rawOrRow.eventId ?? "");
  if (!eventId) return "";

  const isLive = rawOrRow.is_live === true || rawOrRow.isLive === true;
  const scope = isLive ? "live" : "prematch";
  const sport = normToken(rawOrRow.sport ?? "");
  const marketRaw = rawOrRow.market_type || rawOrRow.marketType || rawOrRow.market || "";
  const marketType = normToken(marketRaw);
  const mtKey = sharpMarketKeyForGrouping(marketRaw);

  const isPP = rawOrRow.is_player_prop === true;
  const playerName = isPP ? normToken(rawOrRow.player_name ?? rawOrRow.playerName ?? "") : "";
  const statCategory = isPP ? normToken(rawOrRow.stat_category ?? rawOrRow.statCategory ?? "") : "";

  if (isPP) {
    if (!playerName || !statCategory) return "";
    if (!marketSupportsBestLineGrouping(marketRaw)) return "";
  } else {
    if (!marketSupportsBestLineGrouping(marketRaw)) return "";
    if (marketIsWinnerStyleNoLineVariants(marketRaw)) return "";
  }

  const mode = bestLineMarketGroupingMode(marketRaw);
  if (!mode) return "";

  const legs = Array.isArray(rawOrRow.legs) ? rawOrRow.legs : [];
  const picks = Array.isArray(rawOrRow.picks) ? rawOrRow.picks : [];
  const useLegs = legs.length >= 2 ? legs : picks.length >= 2 ? picks : [];
  if (useLegs.length !== 2) return "";

  const homeTeam = rawOrRow.home_team ?? rawOrRow.homeTeamName ?? "";
  const awayTeam = rawOrRow.away_team ?? rawOrRow.awayTeamName ?? "";

  let totalsTeamCtx = "";
  if (mode === "totals") {
    totalsTeamCtx = detectTotalsTeamContextForBestLineGrouping(rawOrRow, useLegs);
    if (!totalsTeamCtx) return "";
  }

  const canon = useLegs.map((l) => normalizeSelectionForBestLineGrouping(l, mode, homeTeam, awayTeam));
  for (let i = 0; i < canon.length; i += 1) {
    if (!canon[i]) return "";
  }
  const outcomeSig = [...canon].sort((a, b) => a.localeCompare(b)).join("|");

  const periodTok = extractPeriodContextForBestLine(rawOrRow);

  const parts = [
    "arb-bestline",
    "v1",
    scope,
    eventId,
    sport,
    mtKey || marketType,
    isPP ? "pp:1" : "pp:0",
    isPP ? playerName : "",
    isPP ? statCategory : "",
    periodTok ? `p:${periodTok}` : "p:",
    mode === "totals" ? `tctx:${totalsTeamCtx}` : "tctx:",
    `out:${outcomeSig}`,
  ];

  return parts.join("\x1f").trim();
}

function buildSemanticArbitrageIdentity(raw) {
  if (!raw || typeof raw !== "object") return "";

  const eventId = normToken(raw.event_id);
  const sport = normToken(raw.sport);
  const marketType = normToken(raw.market_type || raw.market);
  const scope = raw.is_live === true ? "live" : "prematch";

  const isPlayerProp = raw.is_player_prop === true;
  const playerName = isPlayerProp ? normToken(raw.player_name) : "";
  const statCategory = isPlayerProp ? normToken(raw.stat_category) : "";

  const comparableLine =
    raw.verifiedComparableLine != null
      ? String(raw.verifiedComparableLine).trim()
      : raw.comparableLine != null
        ? String(raw.comparableLine).trim()
        : raw.line != null
          ? String(raw.line).trim()
          : "";

  // Explicit period/map/set/game context tokens from source/backend fields only.
  // Scalar values only — object-typed live-state blobs are excluded.
  // Different period/map/set/game produces a distinct identity even for same market/line.
  function _sc(v) {
    if (v == null || typeof v === "object") return "";
    return String(v).trim();
  }
  const periodCtxParts = [
    _sc(raw.period) ? `period:${_sc(raw.period)}` : "",
    _sc(raw.periodNumber) ? `periodn:${_sc(raw.periodNumber)}` : "",
    _sc(raw.currentPeriod) ? `cperiod:${_sc(raw.currentPeriod)}` : "",
    _sc(raw.half) ? `half:${_sc(raw.half)}` : "",
    _sc(raw.quarter) ? `q:${_sc(raw.quarter)}` : "",
    _sc(raw.quarterNumber) ? `qn:${_sc(raw.quarterNumber)}` : "",
    _sc(raw.currentQuarter) ? `cq:${_sc(raw.currentQuarter)}` : "",
    _sc(raw.inning) ? `inning:${_sc(raw.inning)}` : "",
    _sc(raw.inningNumber) ? `inningn:${_sc(raw.inningNumber)}` : "",
    _sc(raw.currentInning) ? `cinning:${_sc(raw.currentInning)}` : "",
    _sc(raw.map) ? `map:${_sc(raw.map)}` : "",
    _sc(raw.mapNumber) ? `mapn:${_sc(raw.mapNumber)}` : "",
    _sc(raw.set) ? `set:${_sc(raw.set)}` : "",
    _sc(raw.setNumber) ? `setn:${_sc(raw.setNumber)}` : "",
    _sc(raw.game) ? `game:${_sc(raw.game)}` : "",
    _sc(raw.gameNumber) ? `gamen:${_sc(raw.gameNumber)}` : "",
  ].filter(Boolean).join(",");

  const legs = Array.isArray(raw.legs) && raw.legs.length > 0
    ? raw.legs
    : (Array.isArray(raw.picks) ? raw.picks : []);
  const legKeys = legs
    .map((l) => {
      const book = normBook(l?.sportsbook || l?.book);
      const selType = normToken(l?.selection_type);
      const side = normToken(l?.side);
      const sel = normToken(l?.selection || l?.sel);
      const lineTok = pickLineToken(l);
      const line = lineTok ? String(lineTok).trim() : "";
      return [book, selType, side, sel, line].join("|");
    })
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  const parts = [
    "sharp-arb",
    "v1",
    scope,
    eventId,
    sport,
    marketType,
    isPlayerProp ? "pp:1" : "pp:0",
    isPlayerProp ? playerName : "",
    isPlayerProp ? statCategory : "",
    comparableLine ? `line:${comparableLine}` : "line:",
    periodCtxParts ? `ctx:${periodCtxParts}` : "ctx:",
    legKeys.join("~"),
  ];

  const out = parts.join("\x1f").trim();
  return out;
}

function pickPassthroughLiveState(raw) {
  if (!raw || typeof raw !== "object") return {};

  const keys = [
    "status",
    "match_status",
    "matchStatus",
    "state",
    "phase",
    "status_text",
    "statusText",
    "game_status",
    "gameStatus",
    "game_state",
    "period",
    "current_period",
    "currentPeriod",
    "clock",
    "match_clock",
    "matchClock",
    "game_clock",
    "gameClock",
    "time_remaining",
    "timeRemaining",
    "timer",
    "live",
    "scoreboard",
    "elapsed",
    "minute",
    "stoppage",
    "added",
    "addedTime",
    "injuryTime",
    "currentSet",
    "current_set",
    "setNumber",
    "set",
    "quarter",
    "currentQuarter",
    "current_quarter",
    "map",
    "currentMap",
    "mapNumber",
    "game",
    "gameNumber",
  ];

  const out = {};
  const sources = [
    raw,
    raw.live && typeof raw.live === "object" ? raw.live : null,
    raw.timer && typeof raw.timer === "object" ? raw.timer : null,
    raw.scoreboard && typeof raw.scoreboard === "object" ? raw.scoreboard : null,
    raw.period && typeof raw.period === "object" ? raw.period : null,
    raw.game_state && typeof raw.game_state === "object" ? raw.game_state : null,
  ].filter(Boolean);

  for (let si = 0; si < sources.length; si += 1) {
    const src = sources[si];
    for (let ki = 0; ki < keys.length; ki += 1) {
      const k = keys[ki];
      if (out[k] !== undefined) continue;
      if (!Object.prototype.hasOwnProperty.call(src, k)) continue;
      const v = src[k];
      if (v == null) continue;
      out[k] = v;
    }
  }

  return out;
}

/**
 * Sharp's /opportunities/arbitrage does NOT include home_team / away_team fields.
 * Derive them from event_name. Sharp uses American convention "Away @ Home"
 * as the primary format; fallback to "Home vs Away" / "Home – Away" for European.
 */
function parseTeamsFromArbEventName(eventName) {
  const s = String(eventName ?? "").trim();
  if (!s) return { home: "", away: "" };

  // American format: "Away @ Home" (e.g. "Lakers @ Celtics")
  const mAt = /^(.+?)\s+@\s+(.+)$/i.exec(s);
  if (mAt) {
    return { home: mAt[2].trim(), away: mAt[1].trim() };
  }

  // European format: "Home vs Away" / "Home v Away" / "Home – Away" / "Home - Away"
  const mVs =
    /^(.+?)\s+vs\.?\s+(.+)$/i.exec(s) ||
    /^(.+?)\s+v\s+(.+)$/i.exec(s) ||
    /^(.+?)\s+[–\-]\s+(.+)$/.exec(s);
  if (mVs) return { home: mVs[1].trim(), away: mVs[2].trim() };

  return { home: "", away: "" };
}

function normalizeArbitrageRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const providerId = toStr(raw.id, "");
  if (!providerId) return null;
  const semanticId = buildSemanticArbitrageIdentity(raw) || providerId;

  const event_id = toStr(raw.event_id, "");
  const external_event_id = raw.external_event_id != null ? toStr(raw.external_event_id, "") : "";
  const event_name = toStr(raw.event_name ?? raw.eventName ?? raw.match, "");
  const sport = toStr(raw.sport, "");
  const league = toStr(raw.league, "");
  const market_type = toStr(raw.market_type, "");
  const marketFallback = toStr(raw.market, "");
  const market = market_type || marketFallback;
  const line = raw.line != null ? raw.line : null;
  const profit_percent = toNum(raw.profit_percent, 0) || 0;
  const implied_total = toNum(raw.implied_total, null);
  const is_live = toBool(raw.is_live);
  const start_time =
    raw.start_time ??
    raw.startTime ??
    raw.kickoff ??
    raw.kickoffDate ??
    raw.event_start_time ??
    null;
  const is_alternate_line = toBool(raw.is_alternate_line);
  const possibly_stale = toBool(raw.possibly_stale);
  const oldest_odds_age_seconds = toNum(raw.oldest_odds_age_seconds, null);
  const warnings = Array.isArray(raw.warnings) ? raw.warnings : [];
  const game_state = raw.game_state ?? null;
  const ev_available = raw.ev_available === true ? true : raw.ev_available === false ? false : null;
  const ev_percentage = toNum(raw.ev_percentage, null);
  const is_player_prop = toBool(raw.is_player_prop);
  const player_name = raw.player_name ?? null;
  const stat_category = raw.stat_category ?? null;
  const detected_at = raw.detected_at || null;
  const market_id = raw.market_id ?? null;
  // Sharp /opportunities/arbitrage never populates home_team / away_team —
  // derive from event_name. Keep raw fields as primary in case Sharp
  // populates them in the future.
  let home_team = raw.home_team != null ? toStr(raw.home_team, "") : toStr(raw.homeTeamName, "");
  let away_team = raw.away_team != null ? toStr(raw.away_team, "") : toStr(raw.awayTeamName, "");
  if (!home_team || !away_team) {
    const parsed = parseTeamsFromArbEventName(event_name);
    if (!home_team && parsed.home) home_team = parsed.home;
    if (!away_team && parsed.away) away_team = parsed.away;
  }

  const legs = Array.isArray(raw.legs) ? raw.legs : [];
  const picks = legs.map((l) => {
    const sportsbook = toStr(l?.sportsbook, "");
    const selection = toStr(l?.selection, "");
    const odds_decimal = toNum(l?.odds_decimal, null);
    const timestamp = l?.timestamp ?? null;
    const oddsTs = parseTsMs(timestamp);
    const oddsForUi = toNum(odds_decimal ?? l?.odds ?? l?.price, 0) || 0;

    const legLine = l?.line;
    const legHandicap = l?.handicap;
    const legPoint = l?.point;
    const legTotal = l?.total;
    const legOverLine = l?.overLine;
    const legUnderLine = l?.underLine;

    return {
      // ArbitragePage required shape
      book: sportsbook || toStr(l?.book, ""),
      sel: selection || toStr(l?.sel, ""),
      odds: oddsForUi,
      ...(oddsTs !== undefined ? { oddsTs } : {}),
      ...(l?.verifiedLine != null && String(l.verifiedLine).trim() !== ""
        ? { verifiedLine: String(l.verifiedLine).trim() }
        : {}),
      ...(legLine != null && String(legLine).trim() !== "" ? { line: String(legLine).trim() } : {}),
      ...(legHandicap != null && String(legHandicap).trim() !== "" ? { handicap: String(legHandicap).trim() } : {}),
      ...(legPoint != null && String(legPoint).trim() !== "" ? { point: String(legPoint).trim() } : {}),
      ...(legTotal != null && String(legTotal).trim() !== "" ? { total: String(legTotal).trim() } : {}),
      ...(legOverLine != null && String(legOverLine).trim() !== "" ? { overLine: String(legOverLine).trim() } : {}),
      ...(legUnderLine != null && String(legUnderLine).trim() !== "" ? { underLine: String(legUnderLine).trim() } : {}),

      // Official leg fields preserved explicitly
      sportsbook: sportsbook || null,
      selection: selection || null,
      odds_american: l?.odds_american ?? null,
      odds_decimal: odds_decimal,
      implied_probability: toNum(l?.implied_probability, null),
      stake_percent: toNum(l?.stake_percent, null),
      timestamp: timestamp,
      external_event_id: l?.external_event_id ?? null,
      selection_id: l?.selection_id ?? null,
      market_id: l?.market_id ?? null,
      selection_type: l?.selection_type ?? null,
      side: l?.side ?? null,
    };
  });

  const passthroughLiveState = pickPassthroughLiveState(raw);

  const out = {
    id: providerId,
    providerOpportunityId: providerId,
    stableKey: semanticId,
    opportunityFingerprint: semanticId,
    semanticOpportunityFingerprint: semanticId,
    eventId: event_id,
    eventName: event_name,
    match: event_name,
    sport,
    league,
    market,
    marketType: market_type,
    ...(raw.comparableLine != null ? { comparableLine: raw.comparableLine } : {}),
    ...(raw.verifiedComparableLine != null ? { verifiedComparableLine: raw.verifiedComparableLine } : {}),
    line,
    profitPct: profit_percent,
    profitPercent: profit_percent,
    impliedTotal: implied_total,
    isLive: is_live,
    scope: is_live ? "live" : "prematch",
    kickoff: start_time,
    kickoffDate: start_time,
    startTime: start_time,
    detectedAt: detected_at,
    detected_at,
    is_alternate_line,
    possibly_stale,
    oldest_odds_age_seconds,
    warnings,
    game_state,
    ...(raw.period != null ? { period: raw.period } : {}),
    ...(raw.scoreboard != null ? { scoreboard: raw.scoreboard } : {}),
    ...(raw.timer != null ? { timer: raw.timer } : {}),
    ...(raw.live != null ? { live: raw.live } : {}),
    ...passthroughLiveState,
    ev_available,
    ev_percentage,
    is_player_prop,
    player_name,
    stat_category,
    home_team,
    away_team,
    homeTeamName: home_team,
    awayTeamName: away_team,
    picks,
    confidenceScore: possibly_stale ? 45 : 80,
    _source: "sharpapi",

    // Official top-level fields (explicit support)
    event_id,
    external_event_id: external_event_id || undefined,
    event_name,
    sport: sport || "",
    league: league || "",
    market_type: market_type || "",
    home_team,
    away_team,
    ...(market_id != null ? { market_id } : {}),
    profit_percent,
    implied_total,
    is_live,
    start_time,
    possibly_stale,
    detected_at,
  };

  const bestLineGroupKey = buildArbitrageBestLineGroupKey(raw);
  if (bestLineGroupKey) out.bestLineGroupKey = bestLineGroupKey;

  enrichCommercialArbitrageQuality(out);
  return out;
}

// Sharp doc limits — do not exceed.
const SHARP_ARB_MAX_LIMIT = 200;
const SHARP_ARB_MAX_OFFSET = 5000;
const SHARP_ARB_MAX_PAGES = 25; // safety cap; 25 * 200 = 5000 rows max

/**
 * @param {import('axios').AxiosInstance} client
 * @param {{ live: boolean, limit?: number, min_profit?: number }} opts
 * @returns {Promise<object[]>}
 */
async function fetchSharpArbitrageList(client, opts) {
  const live = opts.live === true;
  const ENV_LIMIT = Math.min(
    SHARP_ARB_MAX_LIMIT,
    Number(process.env.SHARP_ARB_FETCH_LIMIT) || SHARP_ARB_MAX_LIMIT
  );
  const pageLimit = Math.min(
    SHARP_ARB_MAX_LIMIT,
    Math.max(1, Number(opts.limit) || ENV_LIMIT)
  );
  const min_profit = toNum(opts.min_profit, 0.5);

  const allRows = [];
  let offset = 0;
  let pages = 0;

  while (pages < SHARP_ARB_MAX_PAGES) {
    if (offset > SHARP_ARB_MAX_OFFSET) break;

    const { data } = await client.get("/opportunities/arbitrage", {
      params: {
        live,
        limit: pageLimit,
        min_profit,
        offset,
      },
    });

    if (!data || typeof data !== "object") break;
    if (data.success === false) break;

    let rows = null;
    if (Array.isArray(data.data)) {
      rows = data.data;
    } else if (data.data && typeof data.data === "object" && Array.isArray(data.data.data)) {
      rows = data.data.data;
    }
    if (!Array.isArray(rows) || rows.length === 0) break;

    for (let i = 0; i < rows.length; i += 1) allRows.push(rows[i]);
    pages += 1;

    // Sharp pagination shape: { limit, offset, has_more, next_offset }
    const pag = data.pagination;
    if (!pag || pag.has_more !== true) break;
    if (pag.next_offset != null && Number.isFinite(Number(pag.next_offset))) {
      offset = Number(pag.next_offset);
    } else {
      offset += pageLimit;
    }
  }

  return allRows.map(normalizeArbitrageRow).filter(Boolean);
}

/* ============================================================
   Arb-only display helpers (display layer; pure formatters).
   These do NOT participate in arbitrage detection, profit
   calculation, filtering, polling, or any backend logic.
   ============================================================ */

/**
 * Returns the raw market identifier from an Arb row, prioritising the
 * normalized Sharp `market_type` and falling back to legacy aliases.
 */
function pickArbDisplayMarketSource(item) {
  if (!item || typeof item !== "object") return "";
  const cands = [item.market_type, item.marketType, item.market];
  for (let i = 0; i < cands.length; i += 1) {
    const s = String(cands[i] ?? "").trim();
    if (s) return s;
  }
  return "";
}

/**
 * Detect a presentation period token (FT/HT/2H/Q1..Q4/Set 1-2/Map 1-3)
 * from a raw market string only — never invents one when not present.
 * Returns "" when no scope is detectable.
 */
function detectArbDisplayPeriodScopeKey(rawMarket) {
  const raw = String(rawMarket ?? "").trim();
  if (!raw) return "";
  const n = raw
    .toLowerCase()
    .replace(/[‐-‒–—−]/g, "-")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (/\b(?:1st|first)\s*quarter\b/.test(n) || /\bq\s*1\b/.test(n)) return "Q1";
  if (/\b(?:2nd|second)\s*quarter\b/.test(n) || /\bq\s*2\b/.test(n)) return "Q2";
  if (/\b(?:3rd|third)\s*quarter\b/.test(n) || /\bq\s*3\b/.test(n)) return "Q3";
  if (/\b(?:4th|fourth)\s*quarter\b/.test(n) || /\bq\s*4\b/.test(n)) return "Q4";

  if (/\b(?:1st|first)\s*set\b/.test(n) || /\bset\s*1\b/.test(n)) return "Set 1";
  if (/\b(?:2nd|second)\s*set\b/.test(n) || /\bset\s*2\b/.test(n)) return "Set 2";
  if (/\b(?:3rd|third)\s*set\b/.test(n) || /\bset\s*3\b/.test(n)) return "Set 3";

  if (/\b(?:1st|first)\s*map\b/.test(n) || /\bmap\s*1\b/.test(n)) return "Map 1";
  if (/\b(?:2nd|second)\s*map\b/.test(n) || /\bmap\s*2\b/.test(n)) return "Map 2";
  if (/\b(?:3rd|third)\s*map\b/.test(n) || /\bmap\s*3\b/.test(n)) return "Map 3";

  if (/\b(?:1st|first)\s*half\b/.test(n) || /\b1h\b/.test(n) || /\bht\b/.test(n)) return "HT";
  if (/\b(?:2nd|second)\s*half\b/.test(n) || /\b2h\b/.test(n)) return "2H";

  if (/\bfull\s*time\b/.test(n) || /\bfulltime\b/.test(n) || /\bfull\s+match\b/.test(n) || /\bft\b/.test(n)) {
    return "FT";
  }

  return "";
}

const ARB_DISPLAY_IMPLICIT_FT_FAMILIES = [
  "moneyline",
  "match winner",
  "winner",
  "1x2",
  "moneyline 3 way",
  "3 way moneyline",
  "three way moneyline",
  "full time result",
  "fulltime result",
  "match odds",
  "spread",
  "point spread",
  "asian handicap",
  "handicap",
  "puck line",
  "run line",
  "totals",
  "total",
  "total points",
  "total goals",
  "total runs",
  "total games",
  "total sets",
  "total corners",
  "corners over under",
  "asian handicap corners",
  "corner match bet",
  "corner winner",
  "team total",
  "team total over under",
  "team total goals",
  "team total points",
  "team total runs",
  "team total corners",
  "set handicap",
  "game handicap",
  "game spread",
];

function arbMarketHasImplicitFullTime(rawMarket, sportTab) {
  const n = String(rawMarket ?? "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!n) return false;
  if (sportTab === "esports") return false;
  for (let i = 0; i < ARB_DISPLAY_IMPLICIT_FT_FAMILIES.length; i += 1) {
    const f = ARB_DISPLAY_IMPLICIT_FT_FAMILIES[i];
    if (n === f || n.startsWith(`${f} `)) return true;
  }
  return false;
}

/**
 * Final period token used in the Arb card.
 * - Returns explicit scope when present in raw market.
 * - Returns "FT" only for clearly full-match families on non-esports rows.
 * - Returns "" when truly unknown (esports without explicit map, etc).
 */
function pickArbDisplayPeriodScope(item, sportTab) {
  const raw = pickArbDisplayMarketSource(item);
  const explicit = detectArbDisplayPeriodScopeKey(raw);
  if (explicit) return explicit;
  if (arbMarketHasImplicitFullTime(raw, sportTab)) return "FT";
  return "";
}

function isMeaningfulLineTokenForArbDisplay(value) {
  const s = String(value ?? "").trim();
  if (!s || s === "—" || s === "-") return false;
  return true;
}

/**
 * Resolves the best line token for a leg, preferring leg-level fields.
 * Falls back to the parent item line only when meaningful (no flipping).
 */
function pickArbDisplayLine(item, pick) {
  const cands = [
    pick && pick.verifiedLine,
    pick && pick.line,
    pick && pick.handicap,
    pick && pick.point,
    pick && pick.total,
    pick && pick.overLine,
    pick && pick.underLine,
  ];
  for (let i = 0; i < cands.length; i += 1) {
    if (isMeaningfulLineTokenForArbDisplay(cands[i])) return String(cands[i]).trim();
  }
  if (item && typeof item === "object") {
    const itemLine = item.line ?? item.point ?? item.handicap ?? item.total;
    if (isMeaningfulLineTokenForArbDisplay(itemLine)) return String(itemLine).trim();
  }
  return "";
}

/**
 * Heuristic: is `label` a raw provider key still containing underscores
 * or one of the well-known Sharp market identifiers? Used to gate fallback
 * humanisation in the UI.
 */
function arbDisplayLooksLikeRawMarketKey(label) {
  const s = String(label ?? "").trim();
  if (!s) return false;
  if (/_/.test(s)) return true;
  return /\b(market_type|total_corners|asian_handicap_corners|corner_match_bet|point_spread|total_goals|total_points|total_games|set_handicap|puck_line|run_line|player_made_threes|first_half|second_half|1st_half|2nd_half|1st_quarter|2nd_quarter|3rd_quarter|4th_quarter|1st_set|2nd_set|map_1|map_2)\b/i.test(s);
}

/** Title-case a snake/space form for safe fallback labels. */
function arbHumanizeRawMarketKey(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return "";
  return s
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ");
}

function formatArbHkOddsNumber(n) {
  if (!Number.isFinite(n)) return "";
  const fixed = n.toFixed(3);
  return fixed.replace(/(?:\.0+|(\.\d*?)0+)$/, "$1");
}

/**
 * Build a HK-format odds string for an Arb leg, supporting decimal,
 * American, and pre-converted HK inputs. Pure (no side effects).
 */
function buildArbPickOddsHkDisplay(pick) {
  if (!pick || typeof pick !== "object") return "";

  const dec = Number(pick.odds_decimal ?? pick.oddsDecimal);
  if (Number.isFinite(dec) && dec > 1) return formatArbHkOddsNumber(dec - 1);

  const am = Number(pick.odds_american ?? pick.oddsAmerican);
  if (Number.isFinite(am) && am !== 0) {
    let asDec;
    if (am > 0) asDec = 1 + am / 100;
    else asDec = 1 + 100 / Math.abs(am);
    if (Number.isFinite(asDec) && asDec > 1) return formatArbHkOddsNumber(asDec - 1);
  }

  const generic = Number(pick.odds);
  if (Number.isFinite(generic)) {
    if (generic > 1) return formatArbHkOddsNumber(generic - 1);
    if (generic > 0 && generic < 1) return formatArbHkOddsNumber(generic);
  }

  const price = Number(pick.price ?? pick.decimalOdds ?? pick.bestOdds);
  if (Number.isFinite(price) && price > 1) return formatArbHkOddsNumber(price - 1);

  return "";
}

/* --------------------
ARB PREFLIGHT CLIENT VALIDATION HELPERS

These mirror the server's POST /api/analysis/arbitrage/preflight contract.
The server is the source of truth; these helpers exist so the UI can
defensively re-check the response before trusting `canArb:true`.
-------------------- */

function arbPreflightServerContractOk(response) {
  if (!response || typeof response !== "object") return false;
  if (response.ok !== true) return false;
  if (response.canArb !== true) return false;
  const matched = response.matchedLegs;
  if (!Array.isArray(matched) || matched.length < 2) return false;
  for (let i = 0; i < matched.length; i += 1) {
    const m = matched[i];
    if (!m || typeof m !== "object") return false;
    const book = String(m.sportsbook ?? m.book ?? "").trim();
    if (!book) return false;
    const dec = Number(m.odds_decimal);
    if (!Number.isFinite(dec) || dec <= 1) return false;
  }
  return true;
}

function _arbPicksFromItem(item) {
  if (!item || typeof item !== "object") return [];
  return Array.isArray(item.picks) ? item.picks : [];
}

function _normalizeBookKey(name) {
  return String(name || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function _safeFiniteNumber(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _arbMarketKey(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "");
}

function _arbLineComparisonMode(marketRaw) {
  const k = _arbMarketKey(marketRaw);
  if (!k) return "strict";

  const isMoneyline =
    k === "moneyline" ||
    k === "1x2" ||
    k === "h2h" ||
    k === "headtohead" ||
    k === "matchwinner" ||
    k === "winner" ||
    k.includes("matchwinner") ||
    k.includes("gamewinner");
  if (isMoneyline) return "no_line";

  const isSpreadLike =
    k.includes("handicap") ||
    k.includes("spread") ||
    k.includes("runline") ||
    k.includes("puckline") ||
    k.includes("gamespread") ||
    k.includes("sethandicap") ||
    k.includes("cornerhandicap") ||
    (k.includes("asian") && k.includes("handicap"));
  if (isSpreadLike) return "signed";

  const isTotalLike =
    k.includes("teamtotal") ||
    (k.includes("team") && k.includes("total")) ||
    k.includes("total") ||
    k.includes("overunder") ||
    k === "ou" ||
    k === "o/u";
  if (isTotalLike) return "unsigned";

  return "strict";
}

/**
 * Re-validate matchedLegs vs the originally shown row.
 * - matchedLegs count must equal picks count
 * - each matched leg must have sportsbook + odds_decimal
 * - if pick line exists, matched line must be compatible (same finite number)
 * - if response.profitPct exists it must be >= 0 and not below an existing row threshold
 */
function validateArbPreflightMatchedRaw(item, response) {
  const details = {};
  if (!item || typeof item !== "object") {
    return { ok: false, reason: "missing_item", details };
  }
  if (!response || typeof response !== "object") {
    return { ok: false, reason: "missing_response", details };
  }
  if (response.canArb !== true) {
    return { ok: false, reason: "not_canarb", details };
  }
  const matched = response.matchedLegs;
  if (!Array.isArray(matched)) {
    return { ok: false, reason: "missing_matched_legs", details };
  }
  const picks = _arbPicksFromItem(item);
  details.picksCount = picks.length;
  details.matchedCount = matched.length;
  if (picks.length < 2) {
    return { ok: false, reason: "bad_pick_count", details };
  }
  if (matched.length !== picks.length) {
    return { ok: false, reason: "matched_count_mismatch", details };
  }

  for (let i = 0; i < matched.length; i += 1) {
    const m = matched[i];
    if (!m || typeof m !== "object") {
      return { ok: false, reason: "bad_matched_leg", details: { ...details, index: i } };
    }
    const book = String(m.sportsbook ?? m.book ?? "").trim();
    if (!book) {
      return { ok: false, reason: "matched_book_missing", details: { ...details, index: i } };
    }
    const dec = Number(m.odds_decimal);
    if (!Number.isFinite(dec) || dec <= 1) {
      return { ok: false, reason: "matched_odds_missing", details: { ...details, index: i } };
    }
  }

  // Optional book parity check between picks[i] and matched[i] when both
  // sides have an identifiable book token; ordering is not guaranteed across
  // server rebuilds, so accept any matching permutation.
  const matchedBookKeys = matched
    .map((m) => _normalizeBookKey(m.sportsbook ?? m.book))
    .filter((k) => !!k);
  const pickBookKeys = picks
    .map((p) => _normalizeBookKey(p && (p.sportsbook ?? p.book)))
    .filter((k) => !!k);
  if (matchedBookKeys.length === picks.length && pickBookKeys.length === picks.length) {
    const a = [...matchedBookKeys].sort();
    const b = [...pickBookKeys].sort();
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) {
        return {
          ok: false,
          reason: "book_set_mismatch",
          details: { ...details, matchedBooks: a, rowBooks: b },
        };
      }
    }
  }

  // Line compatibility check using canonical market + line fields.
  for (let i = 0; i < picks.length; i += 1) {
    const pick = picks[i] && typeof picks[i] === "object" ? picks[i] : null;
    const marketRaw =
      (pick && (pick.market_type ?? pick.marketType ?? pick.market)) ??
      item?.market_type ??
      item?.marketType ??
      item?.market ??
      "";
    const lineMode = _arbLineComparisonMode(marketRaw);
    if (lineMode === "no_line") continue;

    const pickLine = _safeFiniteNumber(pick && (pick.line ?? pick.handicap ?? pick.point ?? pick.total));
    if (pickLine == null) continue;
    // Look for a matched leg with the same book that carries a comparable line.
    const pickBookKey = _normalizeBookKey(pick && (pick.sportsbook ?? pick.book));
    let candidate = null;
    if (pickBookKey) {
      for (let j = 0; j < matched.length; j += 1) {
        const mk = _normalizeBookKey(matched[j].sportsbook ?? matched[j].book);
        if (mk === pickBookKey) {
          candidate = matched[j];
          break;
        }
      }
    }
    if (!candidate) candidate = matched[i] || null;
    if (!candidate) continue;
    const matchedLine = _safeFiniteNumber(candidate.line);
    if (matchedLine == null) continue;

    const lineMismatch =
      lineMode === "unsigned"
        ? Math.abs(Math.abs(matchedLine) - Math.abs(pickLine)) > 1e-6
        : Math.abs(matchedLine - pickLine) > 1e-6;
    if (lineMismatch) {
      return {
        ok: false,
        reason: "line_mismatch",
        details: { ...details, index: i, pickLine, matchedLine },
      };
    }
  }

  // If the row was published with a min row threshold, the response must
  // still beat 0%. We do NOT require parity with the row's stale profitPct.
  if (response.profitPct != null) {
    const rPct = Number(response.profitPct);
    if (!Number.isFinite(rPct) || rPct < 0) {
      return { ok: false, reason: "profit_below_zero", details: { ...details, profitPct: rPct } };
    }
    const rowMin = _safeFiniteNumber(item.minProfitPctShown);
    if (rowMin != null && rPct + 1e-6 < rowMin) {
      return {
        ok: false,
        reason: "profit_below_row_threshold",
        details: { ...details, profitPct: rPct, rowMin },
      };
    }
  }

  return { ok: true, reason: "", details };
}

/* --------------------
ARB SOURCE-VS-RAW MISMATCH DIAGNOSTICS

These helpers compare the source opportunity row (item.* / item.picks) with
the matched raw legs returned by the preflight server (response.matchedLegs)
and refine a generic failure reason into one of the well-known display keys:

  no_raw_match | period_mismatch | market_mismatch | selection_mismatch
  | line_mismatch | odds_mismatch | stale_raw | unprofitable_after_raw

These are display-only diagnostics. They never loosen matching, never claim
canArb / Raw Verified, and never mutate the response. They only refine the
reason string used by the UI badge / button.
-------------------- */

const _ARB_REASON_DISPLAY_KEYS = new Set([
  "no_raw_match",
  "ambiguous_raw_match",
  "period_mismatch",
  "market_mismatch",
  "market_changed",
  "selection_mismatch",
  "selection_changed",
  "line_mismatch",
  "line_changed",
  "odds_mismatch",
  "odds_changed",
  "stale_raw",
  "raw_odds_too_old",
  "missing_timestamp",
  "unprofitable_after_raw",
  "unprofitable",
  "raw_fetch_failed",
  "row_not_found",
]);

/**
 * Map a heterogeneous server / validation reason string into a normalized
 * display key used by the Arb badge / button. Falls back to the original
 * string when already a known display key, or to "no_raw_match" when blank.
 *
 * Display-only — does not change canArb gating or copy/open blocking.
 */
function mapArbPreflightFailureReasonForDisplay(reason) {
  const r = String(reason || "").trim().toLowerCase();
  if (!r) return "no_raw_match";
  if (_ARB_REASON_DISPLAY_KEYS.has(r)) return r;
  if (r === "ambiguous_raw_match") return "no_raw_match";
  if (r === "missing_matched_legs") return "no_raw_match";
  if (r === "missing_matched_raw") return "no_raw_match";
  if (r === "matched_count_mismatch") return "no_raw_match";
  if (r === "bad_matched_leg") return "no_raw_match";
  if (r === "matched_book_missing") return "no_raw_match";
  if (r === "matched_odds_missing") return "odds_mismatch";
  if (r === "book_set_mismatch") return "market_mismatch";
  if (r === "missing_verified_line" || r === "cross_line_verified") return "line_mismatch";
  if (r === "profit_below_zero" || r === "profit_below_row_threshold") return "unprofitable_after_raw";
  if (r === "raw_odds_too_old" || r === "missing_timestamp") return "stale_raw";
  if (r === "raw_fetch_failed") return "raw_fetch_failed";
  if (r === "row_not_found") return "row_not_found";
  return "no_raw_match";
}

/** Snake/space normaliser shared with the family detector below. */
function _arbReasonNormaliseMarketKey(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s./-]+/g, "_")
    .replace(/__+/g, "_")
    .replace(/^_+|_+$/g, "");
}

/** Detect a comparable family token for a market key. Conservative — returns "" when ambiguous. */
function _arbReasonMarketFamilyToken(rawMarket) {
  const k = _arbReasonNormaliseMarketKey(rawMarket);
  if (!k) return "";
  if (k.includes("team_total") || k.includes("teamtotal")) return "team_total";
  if (k.includes("total") || k.includes("over_under") || k === "ou") return "total";
  if (k.includes("spread") || k.includes("handicap") || k.includes("run_line") || k.includes("runline") ||
      k.includes("puck_line") || k.includes("puckline")) return "handicap";
  if (k === "moneyline" || k === "match_winner" || k === "winner" || k === "1x2" ||
      k === "draw_no_bet" || k === "dnb" || k === "h2h" || k.includes("moneyline_3_way") ||
      k === "match_odds") return "moneyline";
  return "";
}

/**
 * Detect an explicit period/scope token from a market string, period field,
 * or scope field. Returns one of: "ft" | "1h" | "2h" | "q1".."q4" | "set1".."set5" | "map1".."map5" | "".
 *
 * Conservative — returns "" when no explicit token is detectable so callers
 * never trigger period_mismatch on ambiguous data.
 */
function _arbReasonDetectPeriodToken(...sources) {
  for (let i = 0; i < sources.length; i += 1) {
    const raw = sources[i];
    if (raw == null || raw === "") continue;
    const n = String(raw)
      .toLowerCase()
      .replace(/[‐-‒–—−]/g, "-")
      .replace(/[_]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!n) continue;

    if (/\b(?:1st|first)\s*half\b/.test(n) || /\b1h\b/.test(n) || /\bht\b/.test(n) || /\bfirst_half\b/.test(n)) return "1h";
    if (/\b(?:2nd|second)\s*half\b/.test(n) || /\b2h\b/.test(n) || /\bsecond_half\b/.test(n)) return "2h";
    if (/\b(?:1st|first)\s*quarter\b/.test(n) || /\bq\s*1\b/.test(n) || /\b1st_quarter\b/.test(n)) return "q1";
    if (/\b(?:2nd|second)\s*quarter\b/.test(n) || /\bq\s*2\b/.test(n) || /\b2nd_quarter\b/.test(n)) return "q2";
    if (/\b(?:3rd|third)\s*quarter\b/.test(n) || /\bq\s*3\b/.test(n) || /\b3rd_quarter\b/.test(n)) return "q3";
    if (/\b(?:4th|fourth)\s*quarter\b/.test(n) || /\bq\s*4\b/.test(n) || /\b4th_quarter\b/.test(n)) return "q4";
    if (/\bset\s*([1-9])\b/.test(n)) {
      const m = /\bset\s*([1-9])\b/.exec(n);
      if (m) return `set${m[1]}`;
    }
    if (/\bmap\s*([1-9])\b/.test(n)) {
      const m = /\bmap\s*([1-9])\b/.exec(n);
      if (m) return `map${m[1]}`;
    }
    if (/\bfull\s*time\b/.test(n) || /\bfulltime\b/.test(n) || /\bft\b/.test(n) || /\bfull\s+match\b/.test(n) ||
        /\bfull_game\b/.test(n)) {
      return "ft";
    }
  }
  return "";
}

/**
 * Detect a period token with implicit-FT fallback when the family is a
 * canonical full-game market (point_spread, total, moneyline, ...) and no
 * explicit period token is present anywhere in the supplied sources.
 *
 * Used only by the source-vs-raw mismatch diagnostic so an obvious source
 * "FT" vs matched "1st_half" combination still surfaces as period_mismatch.
 * Returns "" only when neither an explicit period nor an implicit FT family
 * is detectable — keeping false-positive rate low.
 */
function _arbReasonDetectPeriodTokenWithImplicit(marketRaw, ...extraSources) {
  const explicit = _arbReasonDetectPeriodToken(marketRaw, ...extraSources);
  if (explicit) return explicit;
  // Esports never has implicit FT — map/set scope is part of the bet identity.
  // For every other typical full-game family, default to "ft" so the diagnostic
  // can compare against an explicitly-1H matched leg.
  if (arbMarketHasImplicitFullTime(marketRaw, "")) return "ft";
  return "";
}

/** Normalise a structured selection_type token. Returns "" when ambiguous. */
function _arbReasonNormSelectionType(raw) {
  const t = String(raw ?? "").trim().toLowerCase();
  if (!t) return "";
  if (t === "home" || t === "h" || t === "1" || t === "team1" || t === "t1") return "home";
  if (t === "away" || t === "a" || t === "2" || t === "team2" || t === "t2") return "away";
  if (t === "draw" || t === "d" || t === "x") return "draw";
  if (t === "over" || t === "o") return "over";
  if (t === "under" || t === "u") return "under";
  return "";
}

/**
 * Compare the source opportunity row (`item`) against the preflight
 * `response.matchedLegs` and return a refined display reason when a clear
 * mismatch is detected. Returns "" when no specific mismatch can be proven
 * — callers should keep using the original / generic reason in that case.
 *
 * Strict / conservative by design:
 *   - We never invent a mismatch from missing data on either side.
 *   - We only flag period_mismatch when BOTH sides have an explicit period.
 *   - We only flag market_mismatch when BOTH sides have a detectable family.
 *   - We only flag selection_mismatch when BOTH sides have a structured
 *     selection_type and the booked legs disagree.
 *   - We only flag odds_mismatch when ANY leg's matched odds drifts more
 *     than 5% (relative) from the source pick odds for the same book.
 *   - We never claim line_mismatch here — the existing
 *     validateArbPreflightMatchedRaw already detects that exactly.
 */
function diagnoseArbSourceVsRawMismatch(item, response) {
  const out = { reason: "", details: {} };
  if (!item || typeof item !== "object") return out;
  if (!response || typeof response !== "object") return out;
  const matched = Array.isArray(response.matchedLegs) ? response.matchedLegs : [];
  if (matched.length < 1) return out;
  const picks = Array.isArray(item.picks) ? item.picks : [];

  const sourceMarketRaw = item.market_type || item.marketType || item.market || "";
  const matchedMarketRaw =
    matched.find((m) => m && (m.market_type || m.marketType || m.market))?.market_type ||
    matched.find((m) => m && (m.market_type || m.marketType || m.market))?.marketType ||
    matched.find((m) => m && (m.market_type || m.marketType || m.market))?.market ||
    "";

  const sourcePeriod = _arbReasonDetectPeriodTokenWithImplicit(
    sourceMarketRaw,
    item.period,
    item.scope,
    item.game_state && (item.game_state.period || item.game_state.half || item.game_state.quarter)
  );
  const matchedPeriod = _arbReasonDetectPeriodTokenWithImplicit(
    matchedMarketRaw,
    response.period,
    response.scope,
    matched.find((m) => m && (m.period || m.scope))?.period,
    matched.find((m) => m && (m.period || m.scope))?.scope
  );
  if (sourcePeriod && matchedPeriod && sourcePeriod !== matchedPeriod) {
    out.reason = "period_mismatch";
    out.details = { sourcePeriod, matchedPeriod };
    return out;
  }

  const sourceFamily = _arbReasonMarketFamilyToken(sourceMarketRaw);
  const matchedFamily = _arbReasonMarketFamilyToken(matchedMarketRaw);
  if (sourceFamily && matchedFamily && sourceFamily !== matchedFamily) {
    out.reason = "market_mismatch";
    out.details = { sourceFamily, matchedFamily, sourceMarketRaw, matchedMarketRaw };
    return out;
  }

  // Structured selection_type comparison per matching book key.
  const matchedByBook = new Map();
  for (let i = 0; i < matched.length; i += 1) {
    const m = matched[i];
    if (!m || typeof m !== "object") continue;
    const bk = _normalizeBookKey(m.sportsbook ?? m.book);
    if (bk) matchedByBook.set(bk, m);
  }
  for (let i = 0; i < picks.length; i += 1) {
    const p = picks[i];
    if (!p || typeof p !== "object") continue;
    const bk = _normalizeBookKey(p.sportsbook ?? p.book);
    if (!bk || !matchedByBook.has(bk)) continue;
    const m = matchedByBook.get(bk);
    const srcSt = _arbReasonNormSelectionType(p.selection_type ?? p.side);
    const mSt = _arbReasonNormSelectionType(m.selection_type ?? m.side);
    if (srcSt && mSt && srcSt !== mSt) {
      out.reason = "selection_mismatch";
      out.details = { book: bk, sourceSelectionType: srcSt, matchedSelectionType: mSt };
      return out;
    }
  }

  // Odds drift diagnostic — relative tolerance of 5% from row decimal odds.
  // Strictly informational: the server is the source of truth, this only
  // refines the reason text so the user understands why preflight rejected.
  for (let i = 0; i < picks.length; i += 1) {
    const p = picks[i];
    if (!p || typeof p !== "object") continue;
    const bk = _normalizeBookKey(p.sportsbook ?? p.book);
    if (!bk || !matchedByBook.has(bk)) continue;
    const m = matchedByBook.get(bk);
    const sDec = _safeFiniteNumber(p.odds_decimal ?? p.odds);
    const mDec = _safeFiniteNumber(m.odds_decimal);
    if (sDec == null || mDec == null) continue;
    if (sDec <= 1 || mDec <= 1) continue;
    const rel = Math.abs(mDec - sDec) / Math.max(sDec, 1e-6);
    if (rel > 0.05) {
      out.reason = "odds_mismatch";
      out.details = { book: bk, sourceOdds: sDec, matchedOdds: mDec, relativeDrift: rel };
      return out;
    }
  }

  return out;
}

/**
 * Convenience: take an existing failure reason (from server response /
 * client validator) and refine it via `diagnoseArbSourceVsRawMismatch`.
 * Returns the refined reason when one is detected, otherwise the
 * original normalized display key.
 *
 * The helper never mutates inputs and never changes copy/open gating.
 */
function refineArbFailureReason(item, response, originalReason) {
  const baseDisplay = mapArbPreflightFailureReasonForDisplay(originalReason);
  // Prefer refined source-vs-raw diagnostics over a generic "no_raw_match"
  // / "ambiguous_raw_match" / "client_validation_failed" so the user sees
  // the most actionable message ("Sai hiệp", "Market đã đổi", ...).
  const generic =
    baseDisplay === "no_raw_match" ||
    baseDisplay === "ambiguous_raw_match" ||
    baseDisplay === "market_mismatch" ||
    baseDisplay === "selection_mismatch" ||
    baseDisplay === "line_mismatch" ||
    baseDisplay === "odds_mismatch";
  if (!generic) return baseDisplay;
  const diag = diagnoseArbSourceVsRawMismatch(item, response);
  if (diag && diag.reason) return diag.reason;
  return baseDisplay;
}

/* ============================================================
   ARB LEG DISPLAY HELPERS — pure formatters, display-only.
   These do NOT affect arbitrage detection, profit calculation,
   filtering, polling, websocket, routes, preflight contract,
   or copy/open blocking. They compute family/line/label tokens
   for each leg without auto-flipping signs and without using
   item.line for both legs on handicap/spread markets.
   ============================================================ */

function _arbDisplayMarketKeyToken(item) {
  const raw = pickArbDisplayMarketSource(item);
  return String(raw || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

const _ARB_HANDICAP_MARKET_KEYS = new Set([
  "spread",
  "point_spread",
  "pointspread",
  "handicap",
  "asian_handicap",
  "asianhandicap",
  "asian_handicap_corners",
  "asianhandicapcorners",
  "corners_asian_handicap",
  "cornersasianhandicap",
  "corner_handicap",
  "cornerhandicap",
  "asian_corners_handicap",
  "game_spread",
  "gamespread",
  "set_handicap",
  "sethandicap",
  "run_line",
  "runline",
  "puck_line",
  "puckline",
  "european_handicap",
  "europeanhandicap",
]);

const _ARB_HANDICAP_SUBSTRING_HINTS = [
  "handicap",
  "spread",
  "run_line",
  "runline",
  "puck_line",
  "puckline",
];

const _ARB_TOTAL_MARKET_KEYS = new Set([
  "total",
  "totals",
  "total_points",
  "totalpoints",
  "total_goals",
  "totalgoals",
  "total_runs",
  "totalruns",
  "total_games",
  "totalgames",
  "total_sets",
  "totalsets",
  "total_corners",
  "totalcorners",
  "team_total",
  "teamtotal",
  "team_totals",
  "teamtotals",
  "team_total_goals",
  "teamtotalgoals",
  "team_total_points",
  "teamtotalpoints",
  "team_total_runs",
  "teamtotalruns",
  "team_total_corners",
  "teamtotalcorners",
  // 1st / 2nd half team total variants — all flavours.
  "1st_half_team_total",
  "1sthalfteamtotal",
  "first_half_team_total",
  "firsthalfteamtotal",
  "2nd_half_team_total",
  "2ndhalfteamtotal",
  "second_half_team_total",
  "secondhalfteamtotal",
  "1st_half_team_total_goals",
  "1sthalfteamtotalgoals",
  "first_half_team_total_goals",
  "firsthalfteamtotalgoals",
  "2nd_half_team_total_goals",
  "2ndhalfteamtotalgoals",
  "second_half_team_total_goals",
  "secondhalfteamtotalgoals",
  "1st_half_team_total_points",
  "1sthalfteamtotalpoints",
  "first_half_team_total_points",
  "firsthalfteamtotalpoints",
  "2nd_half_team_total_points",
  "2ndhalfteamtotalpoints",
  "second_half_team_total_points",
  "secondhalfteamtotalpoints",
  "1st_half_team_total_runs",
  "1sthalfteamtotalruns",
  "first_half_team_total_runs",
  "firsthalfteamtotalruns",
  "2nd_half_team_total_runs",
  "2ndhalfteamtotalruns",
  "second_half_team_total_runs",
  "secondhalfteamtotalruns",
  "1st_half_team_total_corners",
  "1sthalfteamtotalcorners",
  "first_half_team_total_corners",
  "firsthalfteamtotalcorners",
  "2nd_half_team_total_corners",
  "2ndhalfteamtotalcorners",
  "second_half_team_total_corners",
  "secondhalfteamtotalcorners",
  "over_under",
  "overunder",
  "ou",
  "1st_half_total_corners",
  "first_half_total_corners",
  "2nd_half_total_corners",
  "second_half_total_corners",
]);

const _ARB_TOTAL_SUBSTRING_HINTS = [
  "total",
  "team_total",
  "teamtotal",
  "over_under",
  "overunder",
];

const _ARB_MONEYLINE_MARKET_KEYS = new Set([
  "moneyline",
  "match_winner",
  "matchwinner",
  "winner",
  "match_odds",
  "matchodds",
  "1x2",
  "three_way_moneyline",
  "threewaymoneyline",
  "moneyline_3_way",
  "moneyline3way",
  "draw_no_bet",
  "drawnobet",
  "dnb",
  "full_time_result",
  "fulltimeresult",
]);

/**
 * Coarse market family classification used for display formatting only.
 * Returns one of: "handicap" | "total" | "moneyline" | "other".
 *
 * Important rule: any market whose key contains "team_total" / "teamtotal"
 * (e.g. team_total_goals, 1st_half_team_total, 2nd_half_team_total_corners)
 * is total-like even when the same string also contains the handicap-like
 * tokens "handicap" or "spread". Sharp's team-total markets are over/under
 * style with an unsigned line, never a signed handicap leg.
 */
function getArbMarketFamily(item) {
  const k = _arbDisplayMarketKeyToken(item);
  if (!k) return "other";

  // Highest-priority guard: any token that mentions team_total is total-like.
  if (k.includes("team_total") || k.includes("teamtotal")) return "total";

  if (_ARB_HANDICAP_MARKET_KEYS.has(k)) return "handicap";
  if (_ARB_TOTAL_MARKET_KEYS.has(k)) return "total";
  if (_ARB_MONEYLINE_MARKET_KEYS.has(k)) return "moneyline";
  for (let i = 0; i < _ARB_TOTAL_SUBSTRING_HINTS.length; i += 1) {
    if (k.includes(_ARB_TOTAL_SUBSTRING_HINTS[i])) {
      // Disambiguate "..._total_..." mixed with handicap unrelated tokens.
      // (team_total cases were already handled above.)
      if (k.includes("handicap") || k.includes("spread")) return "handicap";
      return "total";
    }
  }
  for (let i = 0; i < _ARB_HANDICAP_SUBSTRING_HINTS.length; i += 1) {
    if (k.includes(_ARB_HANDICAP_SUBSTRING_HINTS[i])) return "handicap";
  }
  return "other";
}

function isArbHandicapLikeMarket(item) {
  return getArbMarketFamily(item) === "handicap";
}

function isArbTotalLikeMarket(item) {
  return getArbMarketFamily(item) === "total";
}

/**
 * Narrow predicate used by the leg label formatter: true only when the
 * market is a "team total"-style total (team_total, team_total_goals,
 * 1st_half_team_total, ...). Plain totals (total_corners, total_goals)
 * are intentionally excluded so generic O/U rendering is unchanged.
 */
function isArbTeamTotalLikeMarket(item) {
  if (!isArbTotalLikeMarket(item)) return false;
  const k = _arbDisplayMarketKeyToken(item);
  return k.includes("team_total") || k.includes("teamtotal");
}

function _arbMeaningfulLineToken(value) {
  const s = String(value ?? "").trim();
  if (!s) return false;
  if (s === "—" || s === "-") return false;
  return true;
}

function _arbExtractSelectionTrailingNumber(selectionRaw) {
  const s = String(selectionRaw ?? "").trim();
  if (!s) return "";
  const m =
    /[\s(]([-+]?\d+(?:\.\d+)?)\s*\)?\s*$/.exec(s) ||
    /\s([-+]?\d+(?:\.\d+)?)\s*$/.exec(s);
  if (!m) return "";
  return m[1].trim();
}

/**
 * Resolves the displayed line for a single Arb leg using the safest available
 * source. Order of preference:
 *   1) matchedRawLeg.line (preflight matchedLegs — server truth)
 *   2) pick.verifiedLine
 *   3) pick.line
 *   4) pick.handicap
 *   5) pick.point
 *   6) pick.total
 *   7) pick.overLine / pick.underLine
 *   8) trailing signed number embedded in the selection text (e.g. "Atletico -0.5")
 *   9) item-level line ONLY when family !== "handicap" AND a meaningful single number
 *
 * Critically, this never auto-flips signs and never substitutes item.line
 * for both legs of a handicap/spread market.
 */
function pickArbLegLine(item, pick, matchedRawLeg) {
  if (matchedRawLeg && typeof matchedRawLeg === "object") {
    const ml = matchedRawLeg.line;
    if (_arbMeaningfulLineToken(ml)) return String(ml).trim();
  }
  if (pick && typeof pick === "object") {
    const cands = [
      pick.verifiedLine,
      pick.line,
      pick.handicap,
      pick.point,
      pick.total,
      pick.overLine,
      pick.underLine,
    ];
    for (let i = 0; i < cands.length; i += 1) {
      if (_arbMeaningfulLineToken(cands[i])) return String(cands[i]).trim();
    }

    // Sharp sometimes ships handicap legs as "Atletico Madrid -0.5" with no
    // separate line field. Recover it from the trailing token.
    const sel = String(pick.selection ?? pick.sel ?? "");
    const trailing = _arbExtractSelectionTrailingNumber(sel);
    if (_arbMeaningfulLineToken(trailing)) return trailing;
  }

  const family = getArbMarketFamily(item);
  // Item-level line is fine for total-like and unknown families when it's a
  // single numeric token — but NEVER for handicap markets, where one shared
  // line cannot represent both legs without flipping signs incorrectly.
  if (family !== "handicap" && family !== "moneyline" && item && typeof item === "object") {
    const itemLine =
      item.verifiedComparableLine ?? item.comparableLine ?? item.line ?? item.point ?? item.handicap ?? item.total;
    const s = String(itemLine ?? "").trim();
    if (_arbMeaningfulLineToken(s) && /^[-+]?\d+(?:\.\d+)?$/.test(s)) {
      return s;
    }
  }
  return "";
}

/**
 * Format the line for a leg according to its market family.
 * - handicap: signed (+0.5, -1.0, 0)
 * - total: unsigned (10.5)
 * - other: returned trimmed as-is
 * Returns "" when no usable line is resolved.
 */
function formatArbLegLine(item, pick, matchedRawLeg) {
  const family = getArbMarketFamily(item);
  if (family === "moneyline") return "";

  const raw = pickArbLegLine(item, pick, matchedRawLeg);
  if (!raw) return "";

  const trimmed = String(raw).trim();
  if (family === "total") {
    // Strip a leading explicit "+" (Sharp sometimes emits "+10.5" for over).
    const numMatch = /^[-+]?(\d+(?:\.\d+)?)$/.exec(trimmed);
    if (numMatch) return numMatch[1];
    return trimmed.replace(/^\+/, "");
  }
  if (family === "handicap") {
    const numMatch = /^([-+]?)(\d+(?:\.\d+)?)$/.exec(trimmed);
    if (numMatch) {
      const sign = numMatch[1] || "";
      const mag = numMatch[2];
      const n = Number(`${sign}${mag}`);
      if (Number.isFinite(n)) {
        if (n > 0) return `+${n}`;
        if (n < 0) return `${n}`;
        return "0";
      }
    }
    return trimmed;
  }
  return trimmed;
}

function _arbStripTrailingLineFromSelection(selectionRaw) {
  let s = String(selectionRaw ?? "").trim();
  if (!s) return "";
  // Repeatedly strip trailing "(±N)" or "±N" tokens.
  for (let guard = 0; guard < 8; guard += 1) {
    const prev = s;
    s = s.replace(/\s*\([-+]?\d+(?:\.\d+)?\)\s*$/, "").trim();
    s = s.replace(/\s+[-+]?\d+(?:\.\d+)?\s*$/, "").trim();
    s = s.replace(/\s*\b(?:pk|pickem|pick'?em)\b\s*$/i, "").trim();
    if (s === prev) break;
  }
  return s;
}

/**
 * Decompose a total-market selection string into a { team, side } pair.
 *
 * - Detects an Over/Under (or Tài/Xỉu) token anywhere in the selection.
 * - Captures any text BEFORE that token as the team prefix verbatim
 *   (no team-name invention; if the prefix is empty, team is "").
 * - Falls back to `selection_type` / `side` fields when the selection
 *   text alone does not surface an O/U token.
 *
 * Generic by design — never special-cases any specific team name.
 */
function _arbDecomposeTotalSelection(selectionRaw, selectionTypeRaw) {
  const sel = String(selectionRaw ?? "").trim();
  const stRaw = String(selectionTypeRaw ?? "").trim().toLowerCase();

  let side = "";
  if (stRaw === "over" || stRaw === "o") side = "Over";
  else if (stRaw === "under" || stRaw === "u") side = "Under";

  let team = "";
  if (sel) {
    const m = /^\s*(.*?)\s*\b(over|under|t[àa]i|x[ỉi]u)\b\s*\(?\s*([-+]?\d+(?:\.\d+)?)?\s*\)?\s*$/i.exec(sel);
    if (m) {
      const teamPart = (m[1] || "").trim().replace(/[-\u2013\u2014:|]+\s*$/, "").trim();
      const word = (m[2] || "").toLowerCase();
      if (!side) {
        if (word === "over" || word.startsWith("t")) side = "Over";
        else if (word === "under" || word.startsWith("x")) side = "Under";
      }
      team = teamPart;
    } else if (!side) {
      // No O/U token detected — keep the raw selection so the caller can
      // decide whether to treat it as a team prefix or pass-through.
      team = sel;
    }
  }

  return { team, side };
}

/**
 * Build the visible label for a single Arb leg (selection + line, if any).
 *
 * - Handicap/spread: "<Team> <signed line>"  e.g. "Atletico Madrid -0.5"
 * - Totals: "<Over|Under translated> <unsigned line>"  e.g. "Tài 10.5"
 * - Moneyline / 1x2: just the selection (or Draw / Hòa)
 *
 * `lang` is forwarded to the optional translator (for Over/Under and Draw)
 * but team/selection text is preserved verbatim — we never translate names.
 */
function buildArbLegDisplayLabel(item, pick, matchedRawLeg, lang) {
  const safePick = pick && typeof pick === "object" ? pick : {};
  const selRaw = String(safePick.selection ?? safePick.sel ?? "").trim();
  const family = getArbMarketFamily(item);

  // Try to translate Over/Under and Draw via the supplied translator if any.
  // We accept an optional translator function to avoid a hard dep on the
  // i18n module from this pure adapter file.
  const tr = (lang && typeof lang === "object" && typeof lang.translateSelection === "function")
    ? lang.translateSelection
    : null;
  const langCode =
    lang && typeof lang === "object" && typeof lang.lang === "string"
      ? lang.lang
      : typeof lang === "string"
        ? lang
        : "en-GB";

  if (family === "moneyline") {
    if (!selRaw) return "—";
    if (/^(draw|tie|x|the\s+draw)$/i.test(selRaw)) {
      const drawLabel = tr ? tr("Draw", langCode) : "";
      return drawLabel || "Draw";
    }
    return selRaw;
  }

  if (family === "total") {
    // Always unsigned for totals — never carry "-0.5" through to the UI.
    const lineStr = formatArbLegLine(item, safePick, matchedRawLeg);
    const { team, side } = _arbDecomposeTotalSelection(
      selRaw,
      safePick.selection_type ?? safePick.side
    );

    let translatedSide = "";
    if (side) {
      translatedSide = (tr ? tr(side, langCode) : "") || side;
    }

    // Team total is the case where preserving the team prefix matters.
    const isTeamTotal = isArbTeamTotalLikeMarket(item);

    const parts = [];
    if (isTeamTotal && team) parts.push(team);
    if (translatedSide) {
      parts.push(translatedSide);
    } else if (!isTeamTotal && team) {
      // Plain total (e.g. total_goals) where the selection wasn't an O/U
      // token — preserve the raw selection so we don't drop information.
      parts.push(team);
    }
    if (lineStr) parts.push(lineStr);

    if (parts.length === 0) {
      return selRaw || "—";
    }

    let label = parts.join(" ").trim();

    // For team_total rows where the team prefix could not be safely
    // resolved from the leg, surface a non-blocking hint in vi-VN so the
    // operator double-checks before placing the slip. Never invent a team.
    if (isTeamTotal && !team && translatedSide) {
      const isVi = /^vi/i.test(String(langCode || ""));
      if (isVi) label = `${label} — cần kiểm đội`;
    }

    return label;
  }

  if (family === "handicap") {
    const lineStr = formatArbLegLine(item, safePick, matchedRawLeg);
    const baseRaw = _arbStripTrailingLineFromSelection(selRaw) || selRaw || "—";
    if (!lineStr) return baseRaw;
    return `${baseRaw} ${lineStr}`.trim();
  }

  // Unknown family: prefer pick selection, append line only when leg-derived.
  const lineStr = formatArbLegLine(item, safePick, matchedRawLeg);
  if (!selRaw) return "—";
  if (!lineStr) return selRaw;
  return `${selRaw} ${lineStr}`.trim();
}

/**
 * Build the market header label for an Arb row (display only).
 *
 * IMPORTANT: For handicap-like markets we deliberately DO NOT append the
 * common item.line — each leg displays its own signed line in the leg row,
 * so showing "· 0.5" in the header would be misleading (the two legs use
 * opposite signs of that magnitude).
 *
 * `lang` is forwarded to the optional translator wrapper.
 */
function buildArbMarketDisplayLabel(item, lang) {
  const family = getArbMarketFamily(item);
  const tr =
    lang && typeof lang === "object" && typeof lang.translateMarket === "function"
      ? lang.translateMarket
      : null;
  const langCode =
    lang && typeof lang === "object" && typeof lang.lang === "string"
      ? lang.lang
      : typeof lang === "string"
        ? lang
        : "en-GB";

  const rawMarket = pickArbDisplayMarketSource(item);
  const sportTab = String(item?._sportTab || "").trim() || "";
  const scope = pickArbDisplayPeriodScope(item, sportTab);

  let base = "";
  if (tr) {
    base = String(tr(rawMarket, langCode) || "").trim();
  }
  if (!base || arbDisplayLooksLikeRawMarketKey(base)) {
    base = arbHumanizeRawMarketKey(rawMarket);
  }
  if (!base) base = rawMarket || "";

  // Per the Arb display contract, the common item.line is NEVER embedded
  // into the market header. Each leg row carries its own line:
  //   - handicap legs render the signed leg line (no header line)
  //   - total legs render the unsigned leg line (no header line)
  //   - moneyline / 1x2 markets are line-less
  // This avoids the previous failure mode where team_total markets shipped
  // a signed item.line into the header and a duplicated/conflicting line
  // into the legs (e.g. "team_total · HT · 0.5" + "Tài -0.5").
  // `family` is intentionally read here so future tweaks (per-family
  // header decoration) remain trivial without changing the call sites.
  void family;

  const parts = [];
  if (base) parts.push(base);
  if (scope) {
    const escaped = scope.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`, "i");
    if (!parts.some((p) => re.test(p))) parts.push(scope);
  }

  return parts.filter(Boolean).join(" · ").trim();
}

function _arbCanonicalMarketTypeToken(raw) {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function _arbCanonicalHasValue(v) {
  if (v == null) return false;
  const s = String(v).trim();
  return !!s && s !== "—" && s !== "-";
}

function getArbCanonicalMarketType(item) {
  const cands = [item?.market_type, item?.marketType, item?.market];
  for (let i = 0; i < cands.length; i += 1) {
    if (_arbCanonicalHasValue(cands[i])) return String(cands[i]).trim();
  }
  return "";
}

function getArbCanonicalTopLevelLine(item) {
  if (_arbCanonicalHasValue(item?.line)) return String(item.line).trim();
  return null;
}

function _arbCanonicalPeriodFromToken(mtToken) {
  if (!mtToken) return "";
  if (
    /(^|_)(1st_half|first_half|1h)(_|$)/.test(mtToken) ||
    /\b1st\s*half\b/.test(mtToken.replace(/_/g, " "))
  ) {
    return "HT";
  }
  if (
    /(^|_)(2nd_half|second_half|2h)(_|$)/.test(mtToken) ||
    /\b2nd\s*half\b/.test(mtToken.replace(/_/g, " "))
  ) {
    return "2H";
  }
  const q = /(^|_)(q[1-4]|[1-4](?:st|nd|rd|th)_quarter|first_quarter|second_quarter|third_quarter|fourth_quarter)(_|$)/.exec(
    mtToken
  );
  if (q) {
    if (q[2] === "first_quarter") return "Q1";
    if (q[2] === "second_quarter") return "Q2";
    if (q[2] === "third_quarter") return "Q3";
    if (q[2] === "fourth_quarter") return "Q4";
    if (/1(?:st)?_quarter|q1/.test(q[2])) return "Q1";
    if (/2(?:nd)?_quarter|q2/.test(q[2])) return "Q2";
    if (/3(?:rd)?_quarter|q3/.test(q[2])) return "Q3";
    if (/4(?:th)?_quarter|q4/.test(q[2])) return "Q4";
  }
  const map = /(^|_)(?:map|map_?number)_?(\d+)(_|$)/.exec(mtToken);
  if (map) return `Map ${map[2]}`;
  const set = /(^|_)(?:set|set_?number)_?(\d+)(_|$)/.exec(mtToken);
  if (set) return `Set ${set[2]}`;
  return "";
}

function getArbCanonicalPeriodScope(item) {
  const mtRaw = getArbCanonicalMarketType(item);
  const mt = _arbCanonicalMarketTypeToken(mtRaw);

  const explicit = [
    ["period", item?.period],
    ["scope", item?.scope],
    ["half", item?.half],
    ["quarter", item?.quarter],
    ["map", item?.map],
    ["set", item?.set],
    ["currentPeriod", item?.currentPeriod],
    ["currentQuarter", item?.currentQuarter],
    ["gameNumber", item?.gameNumber],
    ["mapNumber", item?.mapNumber],
    ["setNumber", item?.setNumber],
  ];

  for (let i = 0; i < explicit.length; i += 1) {
    const [sourceKey, rawValue] = explicit[i];
    if (!_arbCanonicalHasValue(rawValue)) continue;
    const token = _arbCanonicalMarketTypeToken(rawValue);
    if (!token) continue;
    if (/^(ft|full|full_game|fulltime|full_time|match)$/.test(token)) {
      return { label: "FT", source: sourceKey };
    }
    if (/^(ht|1h|1st_half|first_half)$/.test(token)) return { label: "HT", source: sourceKey };
    if (/^(2h|2nd_half|second_half)$/.test(token)) return { label: "2H", source: sourceKey };
    if (/^q[1-4]$/.test(token)) return { label: token.toUpperCase(), source: sourceKey };
    const qNum = /^(?:quarter_?)?([1-4])$/.exec(token);
    if (qNum) return { label: `Q${qNum[1]}`, source: sourceKey };
    const hNum = /^([12])(?:nd|st)?_?half$/.exec(token);
    if (hNum) return { label: hNum[1] === "1" ? "HT" : "2H", source: sourceKey };
    const mapNum = /^(?:map_?)?(\d+)$/.exec(token);
    if (mapNum && (sourceKey === "map" || sourceKey === "mapNumber")) {
      return { label: `Map ${mapNum[1]}`, source: sourceKey };
    }
    const setNum = /^(?:set_?)?(\d+)$/.exec(token);
    if (setNum && (sourceKey === "set" || sourceKey === "setNumber")) {
      return { label: `Set ${setNum[1]}`, source: sourceKey };
    }
    const gameNum = /^(?:game_?)?(\d+)$/.exec(token);
    if (gameNum && sourceKey === "gameNumber") return { label: `Game ${gameNum[1]}`, source: sourceKey };
  }

  const fromMarketType = _arbCanonicalPeriodFromToken(mt);
  if (fromMarketType) return { label: fromMarketType, source: "market_type_token" };

  if (mt && getArbCanonicalMarketFamily(item) !== "unknown") {
    return { label: "FT", source: "inferred_full_game" };
  }
  return { label: "", source: "unknown" };
}

function getArbCanonicalMarketFamily(item) {
  const k = _arbCanonicalMarketTypeToken(getArbCanonicalMarketType(item));
  if (!k) return "unknown";
  if (
    k === "moneyline" ||
    k === "1x2" ||
    k === "match_winner" ||
    k === "winner" ||
    k === "h2h" ||
    k === "match_odds"
  ) {
    return "moneyline";
  }
  if (
    k === "team_total" ||
    k === "team_total_points" ||
    k === "team_total_goals" ||
    k === "team_total_corners" ||
    k === "1st_half_team_total" ||
    k.includes("team_total")
  ) {
    return "team_total";
  }
  if (
    k === "total" ||
    k === "total_points" ||
    k === "total_goals" ||
    k === "total_runs" ||
    k === "total_games" ||
    k === "total_corners" ||
    k === "over_under"
  ) {
    return "total";
  }
  if (
    k === "point_spread" ||
    k === "spread" ||
    k === "handicap" ||
    k === "asian_handicap" ||
    k === "run_line" ||
    k === "puck_line" ||
    k === "set_handicap" ||
    k === "corner_handicap" ||
    k === "asian_handicap_corners"
  ) {
    return "handicap";
  }
  if (k.includes("handicap") || k.includes("spread") || k.includes("run_line") || k.includes("puck_line")) {
    return "handicap";
  }
  return "unknown";
}

function _arbCanonicalParseSignedLineFromSelection(selection) {
  const s = String(selection ?? "").trim();
  if (!s) return "";
  const m = /(?:^|\s)([-+]\d+(?:\.\d+)?)\s*$/.exec(s);
  return m ? m[1] : "";
}

function _arbCanonicalFirstLineCandidate(cands) {
  for (let i = 0; i < cands.length; i += 1) {
    if (_arbCanonicalHasValue(cands[i])) return String(cands[i]).trim();
  }
  return "";
}

function _arbCanonicalUnsignedLine(line) {
  const m = /^[-+]?(\d+(?:\.\d+)?)$/.exec(String(line ?? "").trim());
  if (!m) return String(line ?? "").trim();
  return m[1];
}

/** Format a handicap line with an explicit sign: positive → "+N", negative → "-N", zero → "0". */
function _arbFormatHandicapDisplayLine(line) {
  const s = String(line ?? "").trim();
  const m = /^([-+]?)(\d+(?:\.\d+)?)$/.exec(s);
  if (m) {
    const sign = m[1];
    const mag = m[2];
    const n = Number(`${sign}${mag}`);
    if (Number.isFinite(n)) {
      if (n > 0) return `+${n}`;
      if (n < 0) return `${n}`;
      return "0";
    }
  }
  return s;
}

function getArbCanonicalLegLineInfo(item, pick) {
  const family = getArbCanonicalMarketFamily(item);
  const topLevelLine = getArbCanonicalTopLevelLine(item);
  const legLine = _arbCanonicalFirstLineCandidate([
    pick?.line,
    pick?.verifiedLine,
    pick?.handicap,
    pick?.point,
    pick?.total,
    pick?.overLine,
    pick?.underLine,
  ]);
  const selectionSigned = _arbCanonicalParseSignedLineFromSelection(pick?.selection ?? pick?.sel ?? "");

  if (family === "moneyline") {
    return { displayLine: "", source: "none", confidence: "high", topLevelLine };
  }

  if (family === "total" || family === "team_total") {
    const selected = legLine || topLevelLine || "";
    if (!selected) return { displayLine: "", source: "missing", confidence: "low", topLevelLine };
    const source = legLine ? "leg" : "top_level";
    return {
      displayLine: _arbCanonicalUnsignedLine(selected),
      source,
      confidence: "high",
      topLevelLine,
    };
  }

  if (family === "handicap") {
    const signed = legLine || selectionSigned || "";
    if (signed) {
      return { displayLine: _arbFormatHandicapDisplayLine(signed), source: legLine ? "leg" : "selection_text", confidence: "high", topLevelLine };
    }
    if (topLevelLine) {
      return {
        displayLine: "",
        topLevelLine,
        source: "top_level_only",
        confidence: "needs_raw_or_leg_line",
      };
    }
    return { displayLine: "", source: "missing", confidence: "low", topLevelLine };
  }

  if (legLine) return { displayLine: legLine, source: "leg", confidence: "medium", topLevelLine };
  return { displayLine: "", source: "missing", confidence: "low", topLevelLine };
}

function _arbCanonicalHumanizeMarket(raw) {
  return String(raw ?? "")
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function buildArbCanonicalMarketDisplayLabel(item, translatorBag) {
  const rawMarketType = getArbCanonicalMarketType(item);
  const period = getArbCanonicalPeriodScope(item);
  const family = getArbCanonicalMarketFamily(item);
  const topLevelLine = getArbCanonicalTopLevelLine(item);

  const trMarket =
    translatorBag && typeof translatorBag.translateMarket === "function"
      ? translatorBag.translateMarket
      : null;
  const langCode =
    translatorBag && typeof translatorBag.lang === "string" ? translatorBag.lang : "en-GB";
  let label = trMarket ? String(trMarket(rawMarketType, langCode) || "").trim() : "";
  if (!label || arbDisplayLooksLikeRawMarketKey(label)) {
    label = _arbCanonicalHumanizeMarket(rawMarketType);
  }
  if (!label) label = rawMarketType || "";

  if (period.label) {
    const escaped = period.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`, "i");
    if (!re.test(label)) label = `${label} · ${period.label}`;
  }

  return {
    label,
    rawMarketType,
    periodLabel: period.label || "",
    periodSource: period.source || "unknown",
    family,
    topLevelLine,
  };
}

// Hard-coded OU word map used as fallback when no translateSelection is provided.
const _ARB_OU_WORD_MAP = {
  "vi-VN": { over: "Tài", under: "Xỉu" },
};

function _arbCanonicalTranslateOuWord(word, trSel, langCode) {
  const w = String(word ?? "").trim();
  if (!w) return w;
  const norm = w.toLowerCase();
  if (norm !== "over" && norm !== "under") return w;
  if (trSel) {
    const translated = String(trSel(w, langCode) || "").trim();
    if (translated && translated.toLowerCase() !== norm) return translated;
  }
  const fallback = _ARB_OU_WORD_MAP[langCode];
  if (fallback && fallback[norm]) return fallback[norm];
  return w;
}

function _arbCanonicalTranslateGenericSelection(selRaw, translatorBag) {
  const trSel =
    translatorBag && typeof translatorBag.translateSelection === "function"
      ? translatorBag.translateSelection
      : null;
  const langCode =
    translatorBag && typeof translatorBag.lang === "string" ? translatorBag.lang : "en-GB";
  const s = String(selRaw ?? "").trim();
  if (!s) return "";

  // Exact match: over / under / draw / home / away
  if (/^(over|under|draw|home|away)$/i.test(s)) {
    if (s.toLowerCase() === "over" || s.toLowerCase() === "under") {
      return _arbCanonicalTranslateOuWord(s, trSel, langCode);
    }
    if (trSel) return String(trSel(s, langCode) || s).trim();
    return s;
  }

  // Compound: "TeamName Over" / "TeamName Under" — translate only the OU suffix,
  // keeping the team name in its original language.
  const compoundMatch = s.match(/^(.+?)\s+(over|under)$/i);
  if (compoundMatch) {
    const teamPart = compoundMatch[1].trim();
    const ouPart = compoundMatch[2];
    const translatedOu = _arbCanonicalTranslateOuWord(ouPart, trSel, langCode);
    return `${teamPart} ${translatedOu}`.trim();
  }

  return s;
}

function buildArbCanonicalLegDisplayLabel(item, pick, translatorBag) {
  const selection = String(pick?.selection ?? pick?.sel ?? "").trim();
  const sportsbook = String(pick?.sportsbook ?? pick?.book ?? "").trim();
  const oddsDecimal =
    pick?.odds_decimal != null && pick?.odds_decimal !== ""
      ? Number(pick.odds_decimal)
      : pick?.odds != null
        ? Number(pick.odds)
        : null;
  const oddsAmerican =
    pick?.odds_american != null && pick?.odds_american !== ""
      ? String(pick.odds_american).trim()
      : "";
  const family = getArbCanonicalMarketFamily(item);
  const lineInfo = getArbCanonicalLegLineInfo(item, pick);
  const baseSelection = _arbCanonicalTranslateGenericSelection(selection, translatorBag) || selection || "—";

  let label = baseSelection;
  if (family === "moneyline") {
    label = baseSelection;
  } else if (family === "total" || family === "team_total") {
    if (lineInfo.displayLine) {
      label = `${baseSelection} ${lineInfo.displayLine}`.trim();
    }
  } else if (family === "handicap") {
    const cleanSelection = _arbStripTrailingLineFromSelection(baseSelection) || baseSelection;
    if (lineInfo.displayLine) {
      label = `${cleanSelection} ${lineInfo.displayLine}`.trim();
    } else {
      label = cleanSelection || "—";
    }
  } else if (lineInfo.displayLine) {
    label = `${baseSelection} ${lineInfo.displayLine}`.trim();
  }

  return {
    label: label || "—",
    selection: selection || "—",
    lineDisplay: lineInfo.displayLine || "",
    lineSource: lineInfo.source || "missing",
    lineConfidence: lineInfo.confidence || "low",
    topLevelLine: lineInfo.topLevelLine != null ? lineInfo.topLevelLine : getArbCanonicalTopLevelLine(item),
    sportsbook,
    oddsDecimal: Number.isFinite(oddsDecimal) ? oddsDecimal : null,
    oddsAmerican,
  };
}

function buildArbSourceDebugSnapshot(item) {
  const period = getArbCanonicalPeriodScope(item);
  const family = getArbCanonicalMarketFamily(item);
  const legs = Array.isArray(item?.picks) ? item.picks : [];
  return {
    id: item?.id ?? null,
    event_id: item?.event_id ?? item?.eventId ?? null,
    event_name: item?.event_name ?? item?.eventName ?? item?.match ?? null,
    sport: item?.sport ?? null,
    league: item?.league ?? null,
    market_type: getArbCanonicalMarketType(item) || null,
    line: getArbCanonicalTopLevelLine(item),
    profit_percent: item?.profit_percent ?? item?.profitPct ?? null,
    implied_total: item?.implied_total ?? null,
    is_live: item?.is_live ?? item?.isLive ?? null,
    start_time: item?.start_time ?? item?.startTime ?? item?.kickoffDate ?? null,
    possibly_stale: item?.possibly_stale ?? null,
    oldest_odds_age_seconds: item?.oldest_odds_age_seconds ?? null,
    warnings: item?.warnings ?? null,
    detected_at: item?.detected_at ?? null,
    period: {
      label: period.label || "",
      source: period.source || "unknown",
    },
    family,
    legs: legs.map((leg) => ({
      sportsbook: leg?.sportsbook ?? leg?.book ?? null,
      selection: leg?.selection ?? leg?.sel ?? null,
      selection_type: leg?.selection_type ?? null,
      line: _arbCanonicalHasValue(leg?.line) ? String(leg.line).trim() : null,
      odds_american: leg?.odds_american ?? null,
      odds_decimal: leg?.odds_decimal ?? leg?.odds ?? null,
      implied_probability: leg?.implied_probability ?? null,
      stake_percent: leg?.stake_percent ?? null,
      timestamp: leg?.timestamp ?? null,
      external_event_id: leg?.external_event_id ?? null,
      selection_id: leg?.selection_id ?? null,
      market_id: leg?.market_id ?? null,
    })),
  };
}

function _formatHkFromDecimal(dec) {
  const d = Number(dec);
  if (!Number.isFinite(d) || d <= 1) return null;
  return (d - 1).toFixed(3);
}

/**
 * Build a copy-slip text built ONLY from preflight matchedLegs (server truth)
 * plus safe fixture context from the item. Falls back to `originalCopyText`
 * when matchedLegs are missing, but the caller is expected to gate on
 * `arbPreflightServerContractOk(response) === true` first.
 *
 * `lang` is optional and only used by the leg label formatter so the copy
 * text uses the same Tài/Xỉu / unsigned-line normalisation as the on-card
 * display (e.g. team_total emits "Tài 0.5", never "-0.5"). It is purely a
 * display-layer choice — backend payloads, raw match logic, and copy/open
 * blocking are unchanged.
 */
function buildVerifiedArbCopyText(item, response, originalCopyText, lang) {
  if (!arbPreflightServerContractOk(response)) {
    // Mirrors the no-fake-verified-copy contract: when canArb !== true the
    // helper MUST NOT synthesise a Verified slip from stale picks. The
    // outer wrapper (arbPreflightClientAdapter.buildVerifiedArbCopyText)
    // also enforces this; returning "" here is defense-in-depth so any
    // direct caller follows the same rule.
    return "";
  }
  // Hard reject failure-shaped reasons even when canArb claims true. The
  // backend never sets canArb:true on these, but this guards against any
  // future contract drift from synthesising a Verified slip.
  const reasonKey =
    response && response.reason != null
      ? String(response.reason).trim().toLowerCase()
      : "";
  if (
    reasonKey === "unprofitable_after_raw" ||
    reasonKey === "odds_changed" ||
    reasonKey === "odds_mismatch" ||
    reasonKey === "stale_raw" ||
    reasonKey === "raw_odds_too_old"
  ) {
    return "";
  }
  // `originalCopyText` is intentionally ignored on the success branch:
  // the verified slip is built EXCLUSIVELY from response.matchedLegs so
  // the user never copies a stale source-only pick masquerading as
  // Raw Verified. The arg is kept in the signature for back-compat with
  // older callers but is not read past this point.
  void originalCopyText;
  const safeItem = item && typeof item === "object" ? item : {};
  const match = String(safeItem.match || "").trim() || "—";
  const league = String(safeItem.league || "").trim() || "—";
  const market = String(safeItem.market || "").trim() || "—";
  const profit =
    response.profitPct != null && Number.isFinite(Number(response.profitPct))
      ? Number(response.profitPct).toFixed(2)
      : null;

  const lines = [
    `Match: ${match}`,
    `League: ${league}`,
    `Market: ${market}`,
  ];

  const family = getArbMarketFamily(safeItem);
  const matched = Array.isArray(response.matchedLegs) ? response.matchedLegs : [];
  matched.forEach((m, idx) => {
    const book = String(m.sportsbook ?? m.book ?? "").trim() || "—";

    // Re-use the on-card formatter so total-like rows copy "Tài 0.5"
    // instead of "Over -0.5". Falls back to the raw selection / line when
    // family-specific formatting yields nothing meaningful.
    const pseudoPick = {
      selection: m.selection,
      sel: m.selection,
      selection_type: m.selection_type,
      line: m.line,
      verifiedLine: m.verifiedLine,
      handicap: m.handicap,
      point: m.point,
      total: m.total,
      odds_decimal: m.odds_decimal,
      odds_american: m.odds_american,
    };
    const formattedLabel = buildArbLegDisplayLabel(safeItem, pseudoPick, m, lang) || "";
    const sel = formattedLabel && formattedLabel !== "—"
      ? formattedLabel
      : String(m.selection ?? "").trim() || "—";

    let line = "—";
    if (family === "moneyline") {
      line = "—";
    } else {
      const formattedLine = formatArbLegLine(safeItem, pseudoPick, m);
      if (formattedLine) {
        line = formattedLine;
      } else if (m.line != null && String(m.line).trim() !== "") {
        line = String(m.line).trim();
      }
    }

    const dec = Number(m.odds_decimal);
    const decStr = Number.isFinite(dec) && dec > 1 ? dec.toFixed(3) : "—";
    const hk = _formatHkFromDecimal(dec);
    lines.push(
      `Leg ${idx + 1}: ${sel} | Line: ${line} | Book: ${book} | Odds dec: ${decStr}${hk ? ` | HK: ${hk}` : ""}`
    );
  });

  if (profit != null) lines.push(`Profit %: ${profit}`);
  lines.push(`Verified: preflight matchedLegs`);
  return lines.join("\n");
}

/* =====================================================================
 * STABLE ARB RAW-ODDS VERIFY API (server.js calls these only)
 *
 * IMPORTANT: All Arb-specific matching/diagnostic logic must live HERE.
 * server.js must NOT add Arb book/market/period/selection/line rules of
 * its own. To tune verification behaviour edit only this file.
 * ===================================================================== */

const _ARB_RAW_BOOK_ALIASES = Object.freeze({
  pinnacle: "pinnacle",
  pinnaclesports: "pinnacle",
  saba: "saba",
  sabasports: "saba",
  bet365us: "bet365us",
  bet365uk: "bet365uk",
});

function _arbRawNormBook(s) {
  const raw = String(s == null ? "" : s).trim().toLowerCase();
  if (!raw) return "";
  // First try preserving "_" so bet365_us / bet365_uk are detectable.
  const underscored = raw
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/[\s\-]+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  if (underscored === "bet365_us") return "bet365us";
  if (underscored === "bet365_uk") return "bet365uk";

  const stripped = raw.replace(/[^a-z0-9]+/g, "");
  if (Object.prototype.hasOwnProperty.call(_ARB_RAW_BOOK_ALIASES, stripped)) {
    return _ARB_RAW_BOOK_ALIASES[stripped];
  }
  return stripped;
}

function _arbRawNormMarketTok(s) {
  return String(s == null ? "" : s)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function _arbRawStripPeriodPrefix(tok) {
  let t = String(tok || "");
  for (let guard = 0; guard < 4; guard += 1) {
    const prev = t;
    t = t.replace(
      /^(?:1st_half|first_half|2nd_half|second_half|q[1-4]|map_?\d+|set_?\d+|ht|ft|full|fulltime|full_time|full_game|match|game)_/,
      ""
    );
    if (t === prev) break;
  }
  return t;
}

function _arbRawDeriveMarketFamily(tok) {
  const k = _arbRawNormMarketTok(tok);
  if (!k) return "unknown";
  const stripped = _arbRawStripPeriodPrefix(k);

  if (k.includes("team_total") || k.includes("teamtotal")) return "team_total";
  if (k.includes("corner_handicap") || k.includes("asian_handicap_corners")) return "corner_handicap";

  if (
    stripped === "moneyline" ||
    stripped === "match_winner" ||
    stripped === "match_odds" ||
    stripped === "winner" ||
    stripped === "1x2" ||
    stripped === "h2h" ||
    stripped === "draw_no_bet" ||
    stripped === "dnb"
  ) {
    return "moneyline";
  }
  if (
    stripped === "total" ||
    stripped === "over_under" ||
    /^total_/.test(stripped) ||
    stripped.startsWith("total")
  ) {
    return "total";
  }
  if (
    stripped === "point_spread" ||
    stripped === "spread" ||
    stripped === "handicap" ||
    stripped === "asian_handicap" ||
    stripped === "run_line" ||
    stripped === "puck_line" ||
    stripped === "set_handicap" ||
    /handicap/.test(stripped) ||
    /spread/.test(stripped) ||
    /run_line/.test(stripped) ||
    /puck_line/.test(stripped)
  ) {
    return "handicap";
  }
  return "unknown";
}

function _arbRawTotalSubject(tok) {
  // total / total_points / total_goals / total_runs / total_games / total_corners.
  const stripped = _arbRawStripPeriodPrefix(_arbRawNormMarketTok(tok));
  let m = /^team_total(?:_(.+))?$/.exec(stripped);
  if (m) return `team_${m[1] || "default"}`;
  m = /^total(?:_(.+))?$/.exec(stripped);
  if (m) return m[1] || "default";
  m = /^over_under(?:_(.+))?$/.exec(stripped);
  if (m) return m[1] || "default";
  m = /(?:^|_)total_([a-z0-9]+)/.exec(stripped);
  if (m) return m[1];
  return "default";
}

function _arbRawHandicapSubject(tok) {
  const stripped = _arbRawStripPeriodPrefix(_arbRawNormMarketTok(tok));
  if (/corner/.test(stripped)) return "corners";
  if (/booking|card/.test(stripped)) return "cards";
  return "default";
}

function _arbRawDerivePeriod(item, marketTokenRaw) {
  const tok = _arbRawNormMarketTok(marketTokenRaw);
  const explicits = item && typeof item === "object"
    ? [
        item.period,
        item.scope,
        item.half,
        item.quarter,
        item.map,
        item.set,
        item.currentPeriod,
        item.currentQuarter,
        item.gameNumber,
        item.mapNumber,
        item.setNumber,
      ]
    : [];
  for (let i = 0; i < explicits.length; i += 1) {
    const v = explicits[i];
    if (v == null || v === "") continue;
    const t = _arbRawNormMarketTok(v);
    if (!t) continue;
    if (/^(ft|full|full_game|full_time|fulltime|match|game)$/.test(t)) return "FT";
    if (/^(ht|1h|1st_half|first_half)$/.test(t)) return "1H";
    if (/^(2h|2nd_half|second_half)$/.test(t)) return "2H";
    if (/^q[1-4]$/.test(t)) return t.toUpperCase();
    const qNum = /^(?:quarter_?)?([1-4])$/.exec(t);
    if (qNum) return `Q${qNum[1]}`;
    const hNum = /^([12])(?:st|nd)?_?half$/.exec(t);
    if (hNum) return hNum[1] === "1" ? "1H" : "2H";
    const mapNum = /^(?:map_?)?(\d+)$/.exec(t);
    if (mapNum) return `MAP${mapNum[1]}`;
    const setNum = /^(?:set_?)?(\d+)$/.exec(t);
    if (setNum) return `SET${setNum[1]}`;
  }
  if (tok) {
    if (/(?:^|_)(1st_half|first_half|1h)(?:_|$)/.test(tok)) return "1H";
    if (/(?:^|_)(2nd_half|second_half|2h)(?:_|$)/.test(tok)) return "2H";
    const q = /(?:^|_)q([1-4])(?:_|$)/.exec(tok);
    if (q) return `Q${q[1]}`;
    const map = /(?:^|_)map_?(\d+)(?:_|$)/.exec(tok);
    if (map) return `MAP${map[1]}`;
    const setM = /(?:^|_)set_?(\d+)(?:_|$)/.exec(tok);
    if (setM) return `SET${setM[1]}`;
  }
  return "FT";
}

function _arbRawSelectionTeamNorm(s) {
  return String(s == null ? "" : s)
    .trim()
    .toLowerCase()
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function _arbRawStripSignedLineFromSelection(s) {
  let out = String(s == null ? "" : s).trim();
  for (let guard = 0; guard < 8; guard += 1) {
    const prev = out;
    out = out.replace(/\s*\([-+]?\d+(?:\.\d+)?\)\s*$/, "").trim();
    out = out.replace(/\s+[-+]?\d+(?:\.\d+)?\s*$/, "").trim();
    out = out.replace(/\s*\b(?:pk|pickem|pick'?em)\b\s*$/i, "").trim();
    if (out === prev) break;
  }
  return out;
}

function _arbRawParseTrailingSignedNumber(s) {
  const str = String(s == null ? "" : s).trim();
  if (!str) return null;
  const m = /(?:^|\s|\()([-+]?\d+(?:\.\d+)?)\s*\)?\s*$/.exec(str);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? { signed: m[1].startsWith("+") || m[1].startsWith("-") ? Number(m[1]) : Number(m[1]), raw: m[1] } : null;
}

function _arbRawSelectionDirection(rawSel, rawSelType) {
  const blob = `${String(rawSelType || "")} ${String(rawSel || "")}`.toLowerCase();
  if (/\bover\b/.test(blob) || /^o\b/.test(blob)) return "over";
  if (/\bunder\b/.test(blob) || /^u\b/.test(blob)) return "under";
  return "";
}

function _arbRawIsDraw(s) {
  const t = _arbRawSelectionTeamNorm(s);
  return t === "draw" || t === "tie" || t === "x";
}

function _arbRawTeamMatches(legSel, rawSel) {
  const a = _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(legSel));
  const b = _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(rawSel));
  if (!a || !b) return false;
  if (a === b) return true;
  // Conservative containment: one is a strict prefix/suffix of the other (handles short-form fields like "Real" vs "Real Madrid" only when the full name appears in raw).
  if (a.length >= 3 && b.length >= 3) {
    if (b.includes(a) || a.includes(b)) return true;
  }
  return false;
}

function _arbRawFiniteNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _arbRawAbsEq(a, b, eps) {
  const e = Number.isFinite(Number(eps)) ? Number(eps) : 1e-6;
  return Math.abs(a - b) <= e;
}

function _arbRawSignedLineFromLeg(leg) {
  if (!leg || typeof leg !== "object") return null;
  const cands = [leg.line, leg.handicap, leg.point, leg.verifiedLine];
  for (let i = 0; i < cands.length; i += 1) {
    const v = cands[i];
    if (v == null || v === "") continue;
    const s = String(v).trim();
    if (!/^[-+]?\d+(?:\.\d+)?$/.test(s)) continue;
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function _arbRawUnsignedLineFromLeg(leg) {
  const signed = _arbRawSignedLineFromLeg(leg);
  if (signed != null) return Math.abs(signed);
  if (leg && typeof leg === "object") {
    const cands = [leg.total, leg.overLine, leg.underLine];
    for (let i = 0; i < cands.length; i += 1) {
      const n = _arbRawFiniteNum(cands[i]);
      if (n != null) return Math.abs(n);
    }
  }
  return null;
}

function _arbRawTopLevelMagnitude(item) {
  if (!item || typeof item !== "object") return null;
  const cands = [item.line, item.point, item.handicap, item.total, item.verifiedComparableLine, item.comparableLine];
  for (let i = 0; i < cands.length; i += 1) {
    const n = _arbRawFiniteNum(cands[i]);
    if (n != null) return Math.abs(n);
  }
  return null;
}

function _arbRawPickEventId(item) {
  if (!item || typeof item !== "object") return "";
  const cands = [item.event_id, item.eventId, item.external_event_id, item.game_id];
  for (let i = 0; i < cands.length; i += 1) {
    const v = cands[i];
    if (v == null || v === "") continue;
    return String(v).trim();
  }
  return "";
}

function _arbRawPickMarketType(item) {
  if (!item || typeof item !== "object") return "";
  const cands = [item.market_type, item.marketType, item.market];
  for (let i = 0; i < cands.length; i += 1) {
    const v = cands[i];
    if (v == null || v === "") continue;
    return String(v).trim();
  }
  return "";
}

function _arbRawPickLegBook(leg) {
  if (!leg || typeof leg !== "object") return "";
  const cands = [leg.sportsbook, leg.book];
  for (let i = 0; i < cands.length; i += 1) {
    const v = cands[i];
    if (v == null || v === "") continue;
    return String(v).trim();
  }
  return "";
}

function _arbRawTimestampMs(raw) {
  if (!raw || typeof raw !== "object") return null;
  const cands = [raw.last_seen_at, raw.odds_changed_at, raw.timestamp];
  for (let i = 0; i < cands.length; i += 1) {
    const v = cands[i];
    if (v == null || v === "") continue;
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const ms = Date.parse(String(v));
    if (Number.isFinite(ms)) return ms;
  }
  // sharpRawOddsService normalised rows expose .raw with the original payload.
  if (raw.raw && typeof raw.raw === "object") return _arbRawTimestampMs(raw.raw);
  return null;
}

function _arbRawMarketsCompatible(itemMt, rawMt) {
  const fa = _arbRawDeriveMarketFamily(itemMt);
  const fb = _arbRawDeriveMarketFamily(rawMt);
  if (fa === "unknown" || fb === "unknown") return false;
  if (fa !== fb) return false;
  if (fa === "total") {
    if (_arbRawTotalSubject(itemMt) !== _arbRawTotalSubject(rawMt)) return false;
  }
  if (fa === "team_total") {
    // team_total <-> team_total of the same subject; never match plain total.
    if (_arbRawTotalSubject(itemMt) !== _arbRawTotalSubject(rawMt)) return false;
  }
  if (fa === "handicap") {
    if (_arbRawHandicapSubject(itemMt) !== _arbRawHandicapSubject(rawMt)) return false;
  }
  if (fa === "corner_handicap") {
    if (_arbRawHandicapSubject(rawMt) !== "corners") return false;
  }
  return true;
}

function _arbRawSelectionMatches(item, leg, raw, family) {
  const legSel = String(leg && leg.selection != null ? leg.selection : (leg && leg.sel != null ? leg.sel : "")).trim();
  const rawSel = String(raw.selection != null ? raw.selection : "").trim();
  if (!rawSel) return false;

  if (family === "moneyline") {
    if (_arbRawIsDraw(legSel) || _arbRawIsDraw(rawSel)) {
      return _arbRawIsDraw(legSel) && _arbRawIsDraw(rawSel);
    }
    return _arbRawTeamMatches(legSel, rawSel);
  }

  if (family === "total" || family === "team_total") {
    const legSt = leg && leg.selection_type ? String(leg.selection_type) : "";
    const rawSt = raw.selection_type != null ? String(raw.selection_type) : "";
    const legDir = _arbRawSelectionDirection(legSel, legSt);
    const rawDir = _arbRawSelectionDirection(rawSel, rawSt);
    if (!legDir || !rawDir) return false;
    if (legDir !== rawDir) return false;
    if (family === "team_total") {
      // team context must match when present in either side.
      const legTeam = _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(legSel).replace(/\b(over|under|t[àa]i|x[ỉi]u)\b/gi, "").trim());
      const rawTeam = _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(rawSel).replace(/\b(over|under|t[àa]i|x[ỉi]u)\b/gi, "").trim());
      if (legTeam && rawTeam && legTeam !== rawTeam) return false;
    }
    return true;
  }

  if (family === "handicap" || family === "corner_handicap") {
    return _arbRawTeamMatches(legSel, rawSel);
  }

  // Fallback: require selection text match.
  return _arbRawSelectionTeamNorm(legSel) === _arbRawSelectionTeamNorm(rawSel);
}

/**
 * Pure line-matching gate. Family-aware: see TASK B rules.
 * Returns { ok, reason, magnitude, signed, source }.
 */
function _arbRawLineMatches(item, leg, raw, family) {
  const rawLn = _arbRawFiniteNum(raw.line);
  if (family === "moneyline") {
    return { ok: true, reason: null, magnitude: null, signed: null, source: "none" };
  }

  if (family === "total" || family === "team_total") {
    let mag = null;
    let source = "leg";
    const legUnsigned = _arbRawUnsignedLineFromLeg(leg);
    if (legUnsigned != null) {
      mag = legUnsigned;
    } else {
      const top = _arbRawTopLevelMagnitude(item);
      if (top != null) {
        mag = top;
        source = "item_top_level";
      } else {
        // Try to parse from selection text "Over 2.5" / "Under 8".
        const sel = String(leg && leg.selection != null ? leg.selection : "");
        const m = /([-+]?\d+(?:\.\d+)?)\s*$/.exec(sel.trim());
        if (m) {
          const n = Number(m[1]);
          if (Number.isFinite(n)) {
            mag = Math.abs(n);
            source = "selection_text";
          }
        }
      }
    }
    if (mag == null) return { ok: false, reason: "missing_source_line", magnitude: null, signed: null, source: "missing" };
    if (rawLn == null) return { ok: false, reason: "raw_missing_line", magnitude: mag, signed: null, source };
    if (!_arbRawAbsEq(Math.abs(rawLn), mag)) {
      return { ok: false, reason: "line_changed", magnitude: mag, signed: rawLn, source };
    }
    return { ok: true, reason: null, magnitude: mag, signed: rawLn, source };
  }

  if (family === "handicap" || family === "corner_handicap") {
    const legSigned = _arbRawSignedLineFromLeg(leg);
    if (legSigned != null) {
      if (rawLn == null) return { ok: false, reason: "raw_missing_line", magnitude: Math.abs(legSigned), signed: legSigned, source: "leg" };
      if (!_arbRawAbsEq(rawLn, legSigned)) {
        return { ok: false, reason: "line_changed", magnitude: Math.abs(legSigned), signed: legSigned, source: "leg" };
      }
      return { ok: true, reason: null, magnitude: Math.abs(legSigned), signed: legSigned, source: "leg" };
    }
    // No leg-level signed line; try item.line as MAGNITUDE only.
    const top = _arbRawTopLevelMagnitude(item);
    if (top != null) {
      if (rawLn == null) return { ok: false, reason: "raw_missing_line", magnitude: top, signed: null, source: "item_top_level_magnitude" };
      if (!_arbRawAbsEq(Math.abs(rawLn), top)) {
        return { ok: false, reason: "line_changed", magnitude: top, signed: rawLn, source: "item_top_level_magnitude" };
      }
      return { ok: true, reason: null, magnitude: top, signed: rawLn, source: "item_top_level_magnitude" };
    }
    // Last fallback: parse a signed number from the source selection.
    const trailing = _arbRawParseTrailingSignedNumber(leg && leg.selection);
    if (trailing == null) {
      return { ok: false, reason: "missing_source_line", magnitude: null, signed: null, source: "missing" };
    }
    if (rawLn == null) return { ok: false, reason: "raw_missing_line", magnitude: Math.abs(trailing.signed), signed: trailing.signed, source: "selection_text" };
    if (!_arbRawAbsEq(rawLn, trailing.signed)) {
      return { ok: false, reason: "line_changed", magnitude: Math.abs(trailing.signed), signed: trailing.signed, source: "selection_text" };
    }
    return { ok: true, reason: null, magnitude: Math.abs(trailing.signed), signed: trailing.signed, source: "selection_text" };
  }

  // unknown family: pass-through.
  return { ok: true, reason: null, magnitude: null, signed: null, source: "unknown_family" };
}

function _arbRawComputeProfit(decimals) {
  const odds = Array.isArray(decimals) ? decimals : [];
  if (odds.length < 2) return null;
  let inv = 0;
  for (let i = 0; i < odds.length; i += 1) {
    const o = Number(odds[i]);
    if (!Number.isFinite(o) || o <= 1) return null;
    inv += 1 / o;
  }
  if (inv >= 1 || inv <= 0) return null;
  return { invSum: inv, profitPct: (1 / inv - 1) * 100 };
}

/**
 * Build a fetch plan grouped by event for /events/:id/odds calls.
 *
 * @param {Array<object>} rows  normalized Arb source rows
 * @param {object} [options]    { scope, maxRows, maxEvents }
 * @returns {{
 *   rowsToVerify: object[],
 *   events: Array<{eventId:string, marketTypes:string[], sportsbooks:string[], rowIds:string[]}>,
 *   skippedRows: object[],
 *   diagnostics: object
 * }}
 */
function buildArbRawVerifyFetchPlan(rows, options) {
  const opts = options && typeof options === "object" ? options : {};
  const maxRowsRaw = Math.floor(Number(opts.maxRows));
  const maxRows = Number.isFinite(maxRowsRaw) && maxRowsRaw > 0 ? maxRowsRaw : Infinity;
  const maxEventsRaw = Math.floor(Number(opts.maxEvents));
  const maxEvents = Number.isFinite(maxEventsRaw) && maxEventsRaw > 0 ? maxEventsRaw : Infinity;

  const list = Array.isArray(rows) ? rows : [];
  const rowsToVerify = [];
  const skippedRows = [];
  const eventsMap = new Map();

  for (let i = 0; i < list.length; i += 1) {
    if (rowsToVerify.length >= maxRows) {
      skippedRows.push({ idx: i, id: list[i] && list[i].id, reason: "raw_verify_budget_skipped_row" });
      continue;
    }
    const item = list[i];
    if (!item || typeof item !== "object") {
      skippedRows.push({ idx: i, reason: "bad_row" });
      continue;
    }
    const eid = _arbRawPickEventId(item);
    if (!eid) {
      skippedRows.push({ idx: i, id: item.id || null, reason: "missing_event_id" });
      continue;
    }
    const picks = Array.isArray(item.picks) ? item.picks : [];
    if (picks.length < 2) {
      skippedRows.push({ idx: i, id: item.id || null, reason: "bad_pick_count" });
      continue;
    }

    if (!eventsMap.has(eid) && eventsMap.size >= maxEvents) {
      skippedRows.push({ idx: i, id: item.id || null, reason: "raw_verify_budget_skipped_event" });
      continue;
    }

    let entry = eventsMap.get(eid);
    if (!entry) {
      entry = {
        eventId: eid,
        marketTypeSet: new Set(),
        sportsbookSet: new Set(),
        rowIds: [],
      };
      eventsMap.set(eid, entry);
    }
    const mt = _arbRawPickMarketType(item);
    if (mt) entry.marketTypeSet.add(mt);
    for (let pi = 0; pi < picks.length; pi += 1) {
      const book = _arbRawPickLegBook(picks[pi]);
      if (book) entry.sportsbookSet.add(book);
    }
    const rowId = item.id != null ? String(item.id) : `idx:${i}`;
    entry.rowIds.push(rowId);
    rowsToVerify.push(item);
  }

  const events = [];
  for (const entry of eventsMap.values()) {
    events.push({
      eventId: entry.eventId,
      marketTypes: Array.from(entry.marketTypeSet),
      sportsbooks: Array.from(entry.sportsbookSet),
      rowIds: entry.rowIds.slice(),
    });
  }

  return {
    rowsToVerify,
    events,
    skippedRows,
    diagnostics: {
      scope: opts.scope || null,
      sourceRowCount: list.length,
      rowsToVerifyCount: rowsToVerify.length,
      eventsCount: events.length,
      skippedCount: skippedRows.length,
      maxRows: Number.isFinite(maxRows) ? maxRows : null,
      maxEvents: Number.isFinite(maxEvents) ? maxEvents : null,
    },
  };
}

function _arbRawCloneItem(item) {
  try {
    return JSON.parse(JSON.stringify(item));
  } catch {
    return null;
  }
}

/**
 * Find the single best raw odds row for one Arb leg.
 * Returns { match, candidates, reason }.
 */
function _arbRawFindLegMatch(item, leg, rawRows, expectedFamily, expectedPeriod) {
  const list = Array.isArray(rawRows) ? rawRows : [];
  const wantBook = _arbRawNormBook(_arbRawPickLegBook(leg));
  if (!wantBook) return { match: null, candidates: 0, reason: "leg_missing_book" };

  const itemMt = _arbRawPickMarketType(item);
  const family = expectedFamily || _arbRawDeriveMarketFamily(itemMt);
  const period = expectedPeriod || _arbRawDerivePeriod(item, itemMt);

  const reasons = {
    book: 0,
    market: 0,
    period: 0,
    selection: 0,
    line: 0,
    odds: 0,
  };
  const compatibleByLine = [];

  for (let i = 0; i < list.length; i += 1) {
    const r = list[i];
    if (!r || typeof r !== "object") continue;
    if (_arbRawNormBook(r.sportsbook) !== wantBook) {
      reasons.book += 1;
      continue;
    }
    const rawMt = String(r.market_type != null ? r.market_type : (r.market != null ? r.market : "")).trim();
    if (!_arbRawMarketsCompatible(itemMt, rawMt)) {
      reasons.market += 1;
      continue;
    }
    const rawPeriod = _arbRawDerivePeriod(r, rawMt);
    if (rawPeriod !== period) {
      reasons.period += 1;
      continue;
    }
    if (!_arbRawSelectionMatches(item, leg, r, family)) {
      reasons.selection += 1;
      continue;
    }
    const lineGate = _arbRawLineMatches(item, leg, r, family);
    if (!lineGate.ok) {
      reasons.line += 1;
      continue;
    }
    const dec = Number(r.odds_decimal);
    if (!Number.isFinite(dec) || dec <= 1) {
      reasons.odds += 1;
      continue;
    }
    compatibleByLine.push({ raw: r, lineGate });
  }

  if (compatibleByLine.length === 0) {
    let firstReason = "no_raw_match";
    if (!list.length) firstReason = "no_raw_rows";
    else if (reasons.book === list.length) firstReason = "book_not_found";
    else if (reasons.market >= list.length - reasons.book) firstReason = "market_not_found";
    else if (reasons.period > 0 && reasons.line === 0 && reasons.selection === 0) firstReason = "period_mismatch";
    else if (reasons.selection > 0 && reasons.line === 0) firstReason = "selection_mismatch";
    else if (reasons.line > 0) firstReason = "line_changed";
    return { match: null, candidates: 0, reason: firstReason, reasons };
  }
  if (compatibleByLine.length > 1) {
    // Prefer the freshest (largest timestamp) candidate; this is deterministic and conservative.
    compatibleByLine.sort((a, b) => {
      const ta = _arbRawTimestampMs(a.raw) || 0;
      const tb = _arbRawTimestampMs(b.raw) || 0;
      return tb - ta;
    });
  }
  const winner = compatibleByLine[0];
  return { match: winner.raw, lineGate: winner.lineGate, candidates: compatibleByLine.length, reason: null, reasons };
}

function _arbRawDecimal(raw) {
  if (!raw || typeof raw !== "object") return null;
  const dec = Number(raw.odds_decimal);
  return Number.isFinite(dec) && dec > 1 ? dec : null;
}

/**
 * High-level one-row verify.
 * @returns {object} verification result (canonical fields, never UI text)
 */
function verifySingleArbOpportunityWithRawOdds(item, rawOddsRows, options) {
  const opts = options && typeof options === "object" ? options : {};
  const nowMs = Number.isFinite(Number(opts.nowMs)) ? Number(opts.nowMs) : Date.now();
  const minProfitPct = Number.isFinite(Number(opts.minProfitPct)) ? Number(opts.minProfitPct) : 0;
  const maxRawAgeSec = Number.isFinite(Number(opts.maxRawAgeSec)) ? Number(opts.maxRawAgeSec) : 0;
  const allowMissingTs = opts.allowMissingTimestamp === true;
  const isLive = String(opts.scope || "").toLowerCase() === "live";

  const baseDiagnostics = {
    scope: opts.scope || null,
    eventId: _arbRawPickEventId(item),
    marketType: _arbRawPickMarketType(item),
    minProfitPct,
    maxRawAgeSec,
  };

  if (!item || typeof item !== "object") {
    return { ok: false, status: "rejected", reason: "bad_row", item: null, matchedLegs: [], failedLegs: [], diagnostics: baseDiagnostics };
  }

  const itemMt = baseDiagnostics.marketType;
  const family = _arbRawDeriveMarketFamily(itemMt);
  const period = _arbRawDerivePeriod(item, itemMt);
  baseDiagnostics.family = family;
  baseDiagnostics.period = period;

  const eid = baseDiagnostics.eventId;
  if (!eid) {
    return { ok: false, status: "rejected", reason: "missing_event_id", item, matchedLegs: [], failedLegs: [], diagnostics: baseDiagnostics };
  }
  const picks = Array.isArray(item.picks) ? item.picks : [];
  if (picks.length < 2) {
    return { ok: false, status: "rejected", reason: "bad_pick_count", item, matchedLegs: [], failedLegs: [], diagnostics: baseDiagnostics };
  }
  if (!Array.isArray(rawOddsRows) || rawOddsRows.length === 0) {
    return {
      ok: false,
      status: "rejected",
      reason: "raw_fetch_failed",
      item,
      matchedLegs: [],
      failedLegs: picks.map((p) => ({ book: _arbRawPickLegBook(p), selection: p && p.selection, reason: "no_raw_rows" })),
      diagnostics: baseDiagnostics,
    };
  }

  const matchedLegs = [];
  const failedLegs = [];
  let firstReason = null;

  for (let li = 0; li < picks.length; li += 1) {
    const leg = picks[li];
    const legResult = _arbRawFindLegMatch(item, leg, rawOddsRows, family, period);
    if (legResult.match) {
      const dec = _arbRawDecimal(legResult.match);
      if (dec == null) {
        failedLegs.push({
          book: _arbRawPickLegBook(leg),
          selection: leg && leg.selection,
          reason: "raw_invalid_odds",
        });
        if (!firstReason) firstReason = "raw_invalid_odds";
        matchedLegs.push(null);
        continue;
      }
      const ts = _arbRawTimestampMs(legResult.match);
      matchedLegs.push({ raw: legResult.match, lineGate: legResult.lineGate, decimal: dec, timestampMs: ts });
    } else {
      failedLegs.push({
        book: _arbRawPickLegBook(leg),
        selection: leg && leg.selection,
        reason: legResult.reason || "no_raw_match",
        candidateCount: legResult.candidates || 0,
      });
      if (!firstReason) firstReason = legResult.reason || "no_raw_match";
      matchedLegs.push(null);
    }
  }

  if (failedLegs.length > 0) {
    return {
      ok: false,
      status: "rejected",
      reason: firstReason || "no_raw_match",
      item,
      matchedLegs: matchedLegs.map((m) => (m ? _arbRawDescribeMatched(m) : null)).filter(Boolean),
      failedLegs,
      diagnostics: baseDiagnostics,
    };
  }

  // Freshness gate.
  for (let li = 0; li < matchedLegs.length; li += 1) {
    const m = matchedLegs[li];
    if (!m) continue;
    if (m.timestampMs == null) {
      if (isLive) {
        return {
          ok: false,
          status: "rejected",
          reason: "stale_raw",
          item,
          matchedLegs: matchedLegs.map((mm) => _arbRawDescribeMatched(mm)),
          failedLegs: [],
          diagnostics: { ...baseDiagnostics, freshness: "missing_timestamp" },
        };
      }
      if (!allowMissingTs) {
        return {
          ok: false,
          status: "rejected",
          reason: "stale_raw",
          item,
          matchedLegs: matchedLegs.map((mm) => _arbRawDescribeMatched(mm)),
          failedLegs: [],
          diagnostics: { ...baseDiagnostics, freshness: "missing_timestamp" },
        };
      }
      continue;
    }
    if (maxRawAgeSec > 0) {
      const ageSec = Math.max(0, (nowMs - m.timestampMs) / 1000);
      if (ageSec > maxRawAgeSec) {
        return {
          ok: false,
          status: "rejected",
          reason: "stale_raw",
          item,
          matchedLegs: matchedLegs.map((mm) => _arbRawDescribeMatched(mm)),
          failedLegs: [],
          diagnostics: { ...baseDiagnostics, freshness: "too_old", worstAgeSec: ageSec },
        };
      }
    }
  }

  // Recompute profit using raw decimals.
  const decimals = matchedLegs.map((m) => m.decimal);
  const arb = _arbRawComputeProfit(decimals);
  if (!arb) {
    return {
      ok: false,
      status: "rejected",
      reason: "unprofitable_after_raw",
      item,
      matchedLegs: matchedLegs.map((mm) => _arbRawDescribeMatched(mm)),
      failedLegs: [],
      diagnostics: { ...baseDiagnostics, recomputedProfitPct: null },
    };
  }
  if (arb.profitPct < minProfitPct) {
    return {
      ok: false,
      status: "rejected",
      reason: "unprofitable_after_raw",
      item,
      matchedLegs: matchedLegs.map((mm) => _arbRawDescribeMatched(mm)),
      failedLegs: [],
      profitPct: arb.profitPct,
      diagnostics: { ...baseDiagnostics, recomputedProfitPct: arb.profitPct },
    };
  }

  // Build verified clone with updated raw line/odds/profit.
  const next = _arbRawCloneItem(item);
  if (!next) {
    return {
      ok: false,
      status: "rejected",
      reason: "clone_failed",
      item,
      matchedLegs: matchedLegs.map((mm) => _arbRawDescribeMatched(mm)),
      failedLegs: [],
      diagnostics: baseDiagnostics,
    };
  }
  const nextPicks = Array.isArray(next.picks) ? next.picks : [];
  const verifiedAt = Date.now();
  for (let i = 0; i < nextPicks.length; i += 1) {
    const p = nextPicks[i];
    const m = matchedLegs[i];
    if (!p || !m) continue;
    const r = m.raw;
    const prevDecimal = Number(p.odds_decimal != null ? p.odds_decimal : p.odds);
    const rawDecimal = m.decimal != null ? Number(m.decimal) : null;
    const oddsDiffers = Number.isFinite(rawDecimal) && Number.isFinite(prevDecimal)
      ? Math.abs(rawDecimal - prevDecimal) > 1e-9
      : true;
    p.odds = m.decimal;
    p.odds_decimal = r.odds_decimal != null ? r.odds_decimal : m.decimal;
    if (r.odds_american != null) p.odds_american = r.odds_american;
    if (r.last_seen_at != null) p.lastSeenAt = r.last_seen_at;
    if (m.timestampMs != null) p.oddsTs = m.timestampMs;
    if (r.line != null) {
      p.line = r.line;
      p.verifiedLine = String(r.line);
      delete p.handicap;
      delete p.point;
      delete p.total;
      delete p.overLine;
      delete p.underLine;
    }
    if (r.selection != null) {
      p.selection = String(r.selection);
      p.sel = String(r.selection);
    }
    if (r.selection_type != null) p.selection_type = r.selection_type;
    p.rawVerified = true;
    p.rawLastSeenAt = r.last_seen_at != null ? r.last_seen_at : (m.timestampMs != null ? m.timestampMs : verifiedAt);
    if (oddsDiffers) {
      p.rawOddsChangedAt = m.timestampMs != null ? m.timestampMs : verifiedAt;
    } else if (p.rawOddsChangedAt == null) {
      p.rawOddsChangedAt = p.rawLastSeenAt;
    }
  }
  next.profitPct = arb.profitPct;
  next.profit_percent = arb.profitPct;
  next.profitPercent = arb.profitPct;
  next.implied_total = arb.invSum;
  next.impliedTotal = arb.invSum;
  next.possibly_stale = false;
  next.verifiedLiveOdds = isLive ? true : next.verifiedLiveOdds === true;
  next.rawVerifiedAt = verifiedAt;
  next.rawVerificationStatus = "raw_verified";
  const describedMatched = matchedLegs.map((mm) => _arbRawDescribeMatched(mm));
  next.rawVerification = {
    status: "verified",
    scope: opts.scope || null,
    recomputedProfitPct: arb.profitPct,
    matchedLegs: describedMatched,
    verifiedAt,
  };

  return {
    ok: true,
    status: "verified",
    reason: "raw_verified",
    item: next,
    matchedLegs: describedMatched,
    failedLegs: [],
    profitPct: arb.profitPct,
    impliedTotal: arb.invSum,
    diagnostics: { ...baseDiagnostics, recomputedProfitPct: arb.profitPct },
  };
}

function _arbRawPickContextToken(item, keys) {
  if (!item || typeof item !== "object") return "";
  const list = Array.isArray(keys) ? keys : [];
  for (let i = 0; i < list.length; i += 1) {
    const v = item[list[i]];
    if (v == null || v === "") continue;
    const norm = _arbRawNormMarketTok(v);
    if (norm) return norm;
  }
  // Fallback: read from item.raw (normalised rows carry original payload in .raw).
  const inner = item.raw && typeof item.raw === "object" && !Array.isArray(item.raw) ? item.raw : null;
  if (inner) {
    for (let i = 0; i < list.length; i += 1) {
      const v = inner[list[i]];
      if (v == null || v === "") continue;
      const norm = _arbRawNormMarketTok(v);
      if (norm) return norm;
    }
  }
  return "";
}

function _arbRawDescribeMatchedForReverify(raw, decimal, timestampMs, lineSource) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    sportsbook: r.sportsbook != null ? String(r.sportsbook) : null,
    market_type: r.market_type != null ? String(r.market_type) : null,
    selection: r.selection != null ? String(r.selection) : null,
    selection_type: r.selection_type != null ? String(r.selection_type) : null,
    line: r.line != null ? r.line : null,
    odds_decimal: Number.isFinite(Number(decimal))
      ? Number(decimal)
      : (r.odds_decimal != null ? Number(r.odds_decimal) : null),
    odds_american: r.odds_american != null ? r.odds_american : null,
    last_seen_at: r.last_seen_at != null ? r.last_seen_at : null,
    timestampMs: Number.isFinite(Number(timestampMs)) ? Number(timestampMs) : null,
    lineSource: lineSource || null,
  };
}

function _arbRawReverifyPickLine(leg) {
  if (!leg || typeof leg !== "object") return null;
  const cands = [
    leg.line,
    leg.verifiedLine,
    leg.handicap,
    leg.point,
    leg.total,
    leg.overLine,
    leg.underLine,
  ];
  for (let i = 0; i < cands.length; i += 1) {
    const v = cands[i];
    if (v == null || v === "") continue;
    return String(v);
  }
  return null;
}

function _arbRawReverifySourceLegSummary(leg) {
  const safe = leg && typeof leg === "object" ? leg : {};
  return {
    book: _arbRawPickLegBook(safe) || null,
    selection: safe.selection != null ? String(safe.selection) : (safe.sel != null ? String(safe.sel) : null),
    line: _arbRawReverifyPickLine(safe),
  };
}

function _arbRawReverifyRawLegSummary(raw, status, rejectReason) {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    status: status || null,
    rejectReason: rejectReason || null,
    sportsbook: r.sportsbook != null ? String(r.sportsbook) : null,
    selection: r.selection != null ? String(r.selection) : null,
    line: r.line != null ? r.line : null,
    market_type: r.market_type != null ? String(r.market_type) : (r.market != null ? String(r.market) : null),
    selection_type: r.selection_type != null ? String(r.selection_type) : null,
    odds_decimal: Number.isFinite(Number(r.odds_decimal)) ? Number(r.odds_decimal) : null,
  };
}

function _arbRawReverifyLegOutcomeKey(leg, family) {
  const safe = leg && typeof leg === "object" ? leg : {};
  const legSel = String(
    safe.selection != null ? safe.selection : (safe.sel != null ? safe.sel : "")
  ).trim();
  if (!legSel) return "";

  if (family === "total" || family === "team_total") {
    const dir = _arbRawSelectionDirection(legSel, safe.selection_type);
    if (!dir) return "";
    return dir;
  }

  if (family === "moneyline") {
    if (_arbRawIsDraw(legSel)) return "draw";
    const team = _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(legSel));
    return team || "";
  }

  if (family === "handicap" || family === "corner_handicap") {
    const team = _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(legSel));
    if (!team) return "";
    const signed = _arbRawSignedLineFromLeg(safe);
    if (signed == null) return team;
    return `${team}|${signed}`;
  }

  return _arbRawSelectionTeamNorm(legSel);
}

function _arbRawReverifyRawOutcomeKey(raw, family) {
  const safe = raw && typeof raw === "object" ? raw : {};
  const rawSel = String(safe.selection != null ? safe.selection : "").trim();
  if (!rawSel) return "";

  if (family === "total" || family === "team_total") {
    const dir = _arbRawSelectionDirection(rawSel, safe.selection_type);
    return dir || "";
  }

  if (family === "moneyline") {
    if (_arbRawIsDraw(rawSel)) return "draw";
    return _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(rawSel));
  }

  if (family === "handicap" || family === "corner_handicap") {
    const team = _arbRawSelectionTeamNorm(_arbRawStripSignedLineFromSelection(rawSel));
    if (!team) return "";
    const line = _arbRawFiniteNum(safe.line);
    if (line == null) return team;
    return `${team}|${line}`;
  }

  return _arbRawSelectionTeamNorm(rawSel);
}

function _arbRawReverifyIsThreeWayMoneyline(marketType, picks, rawRows, eventId, period) {
  const mt = _arbRawStripPeriodPrefix(_arbRawNormMarketTok(marketType));
  if (mt === "draw_no_bet" || mt === "dnb") return false;

  let drawSeen = false;
  const src = Array.isArray(picks) ? picks : [];
  for (let i = 0; i < src.length; i += 1) {
    const sel = src[i] && src[i].selection != null ? src[i].selection : (src[i] && src[i].sel != null ? src[i].sel : "");
    if (_arbRawIsDraw(sel)) {
      drawSeen = true;
      break;
    }
  }
  if (drawSeen) return true;

  const raws = Array.isArray(rawRows) ? rawRows : [];
  for (let i = 0; i < raws.length; i += 1) {
    const raw = raws[i];
    if (!raw || typeof raw !== "object") continue;
    if (eventId) {
      const rawEventId = _arbRawPickEventId(raw);
      if (rawEventId && String(rawEventId) !== String(eventId)) continue;
    }
    const rawMt = String(raw.market_type != null ? raw.market_type : (raw.market != null ? raw.market : "")).trim();
    if (!_arbRawMarketsCompatible(marketType, rawMt)) continue;
    if (_arbRawDerivePeriod(raw, rawMt) !== period) continue;
    if (_arbRawIsDraw(raw.selection)) return true;
  }
  return false;
}

function _arbRawReverifyRequiredPickCount(marketType, family, picks, rawRows, eventId, period) {
  if (family === "total" || family === "team_total") return 2;
  if (family === "moneyline") {
    return _arbRawReverifyIsThreeWayMoneyline(marketType, picks, rawRows, eventId, period) ? 3 : 2;
  }
  if (family === "handicap" || family === "corner_handicap") return 2;
  return 2;
}

function _arbRawBuildBadPickCountDiagnostics(sourceRow, picks, rawRows, context) {
  const ctx = context && typeof context === "object" ? context : {};
  const family = ctx.family || "unknown";
  const marketType = ctx.marketType || "";
  const eventId = ctx.eventId || "";
  const period = ctx.period || "FT";
  const sourceList = Array.isArray(picks) ? picks : [];
  const sourceLegs = sourceList.map((leg) => _arbRawReverifySourceLegSummary(leg));

  const sourceOutcomes = new Set();
  let sourceOver = 0;
  let sourceUnder = 0;
  let sourceDraw = 0;
  for (let i = 0; i < sourceList.length; i += 1) {
    const k = _arbRawReverifyLegOutcomeKey(sourceList[i], family);
    if (!k) continue;
    sourceOutcomes.add(k);
    if (k === "over") sourceOver += 1;
    else if (k === "under") sourceUnder += 1;
    else if (k === "draw") sourceDraw += 1;
  }

  const requiredPickCount = _arbRawReverifyRequiredPickCount(
    marketType,
    family,
    sourceList,
    rawRows,
    eventId,
    period
  );

  const acceptedRaw = [];
  const rejectedRaw = [];
  let acceptedRawCount = 0;
  let rejectedRawCount = 0;
  const rawOutcomeSet = new Set();
  const list = Array.isArray(rawRows) ? rawRows : [];
  for (let i = 0; i < list.length; i += 1) {
    const raw = list[i];
    if (!raw || typeof raw !== "object") continue;

    let rejectReason = "";
    if (eventId) {
      const rawEventId = _arbRawPickEventId(raw);
      if (rawEventId && String(rawEventId) !== String(eventId)) rejectReason = "event_mismatch";
    }

    const rawMt = String(raw.market_type != null ? raw.market_type : (raw.market != null ? raw.market : "")).trim();
    if (!rejectReason && !_arbRawMarketsCompatible(marketType, rawMt)) rejectReason = "market_mismatch";
    if (!rejectReason) {
      const rawPeriod = _arbRawDerivePeriod(raw, rawMt);
      if (rawPeriod !== period) rejectReason = "period_mismatch";
    }
    if (!rejectReason) {
      const dec = Number(raw.odds_decimal);
      if (!Number.isFinite(dec) || dec <= 1) rejectReason = "invalid_odds";
    }

    const outcomeKey = !rejectReason ? _arbRawReverifyRawOutcomeKey(raw, family) : "";
    if (!rejectReason && !outcomeKey) rejectReason = "selection_unclassified";

    if (rejectReason) {
      rejectedRawCount += 1;
      if (rejectedRaw.length < 8) {
        rejectedRaw.push(_arbRawReverifyRawLegSummary(raw, "rejected", rejectReason));
      }
      continue;
    }

    acceptedRawCount += 1;
    rawOutcomeSet.add(outcomeKey);
    if (acceptedRaw.length < 8) {
      acceptedRaw.push(_arbRawReverifyRawLegSummary(raw, "accepted", null));
    }
  }

  let structureOk = sourceList.length >= requiredPickCount;
  if (family === "total" || family === "team_total") {
    structureOk = structureOk && sourceOver > 0 && sourceUnder > 0;
  } else if (family === "moneyline") {
    const nonDrawOutcomes = Array.from(sourceOutcomes).filter((k) => k !== "draw");
    if (requiredPickCount >= 3) {
      structureOk = structureOk && sourceDraw > 0 && nonDrawOutcomes.length >= 2;
    } else {
      structureOk = structureOk && nonDrawOutcomes.length >= 2;
    }
  } else {
    structureOk = structureOk && sourceOutcomes.size >= requiredPickCount;
  }

  return {
    sourcePickCount: sourceList.length,
    rawCandidateCount: acceptedRawCount + rejectedRawCount,
    matchedLegCount: sourceOutcomes.size,
    requiredPickCount,
    market_type: marketType || null,
    event_id: eventId || null,
    sourceLegs,
    rawCandidateSample: {
      accepted: acceptedRaw,
      rejected: rejectedRaw,
    },
    sourceOutcomeKeys: Array.from(sourceOutcomes),
    rawOutcomeKeys: Array.from(rawOutcomeSet),
    structureOk,
  };
}

/**
 * Derive per-stage compatibility flags for reverify diagnostics.
 * Reads only from existing rejection reasons + candidate sample — no logic changes.
 * Returns an object with keys: event, book, market, period, selection,
 * oddsValid, oddsFresh, line — each true/false/"unknown".
 */
function _arbRawReverifyDeriveCompatibility(reason, rejectedRawCandidates, failedLegIndex) {
  const U = "unknown";
  const fullTrue = { event: true, book: true, market: true, period: true, selection: true, oddsValid: true, oddsFresh: true, line: true };
  const allUnknown = { event: U, book: U, market: U, period: U, selection: U, oddsValid: U, oddsFresh: U, line: U };

  if (reason === "raw_reverified" || reason === "unprofitable_after_raw" || reason === "profit_below_min") {
    return Object.assign({}, fullTrue);
  }
  if (reason === "bad_source_row" || reason === "raw_fetch_failed" || reason === "bad_pick_count") {
    return Object.assign({}, allUnknown);
  }
  if (reason === "missing_event_id") {
    return Object.assign({}, allUnknown, { event: false });
  }
  if (reason === "book_not_found") {
    return Object.assign({}, allUnknown, { event: true, book: false });
  }

  if (
    (reason === "no_compatible_raw_legs" || reason === "ambiguous_market_period_line") &&
    Array.isArray(rejectedRawCandidates) && rejectedRawCandidates.length > 0
  ) {
    const stageOrder = ["book", "event", "market", "period", "selection", "oddsValid", "oddsFresh", "line"];
    const reasonToStage = {
      book_mismatch: "book",
      event_mismatch: "event",
      market_mismatch: "market",
      period_mismatch: "period",
      explicit_period_token_mismatch: "period",
      explicit_phase_token_mismatch: "period",
      selection_mismatch: "selection",
      bad_decimal_odds: "oddsValid",
      missing_timestamp: "oddsFresh",
      stale_raw: "oddsFresh",
      line_mismatch: "line",
    };
    // Use per-leg sample when legIndex is available, fall back to full sample.
    const legSample = failedLegIndex != null
      ? rejectedRawCandidates.filter((r) => r && r.legIndex === failedLegIndex)
      : [];
    const useSample = legSample.length > 0 ? legSample : rejectedRawCandidates;
    // Find the most-advanced stage that any candidate was rejected at.
    // Stages before it were passed by at least one candidate.
    let maxStageIdx = -1;
    for (const r of useSample) {
      const stage = r && r.reason ? reasonToStage[String(r.reason)] : undefined;
      if (stage) {
        const idx = stageOrder.indexOf(stage);
        if (idx > maxStageIdx) maxStageIdx = idx;
      }
    }
    if (maxStageIdx >= 0) {
      const result = {};
      for (let si = 0; si < stageOrder.length; si += 1) {
        const s = stageOrder[si];
        if (si < maxStageIdx) result[s] = true;
        else if (si === maxStageIdx) result[s] = false;
        else result[s] = U;
      }
      return result;
    }
  }

  return Object.assign({}, allUnknown);
}

/**
 * Build a compact diagnostics object for reverifyArbOpportunityFromFreshRawOdds returns.
 * Only surfaces existing decision information — no logic changes.
 */
function _arbRawReverifyBuildDiagnostics(params) {
  const p = params && typeof params === "object" ? params : {};
  const finalStage = p.finalStage != null ? String(p.finalStage) : "unknown";
  const reason = p.reason != null ? String(p.reason) : "unknown";
  const matchedLegCount = Number.isFinite(Number(p.matchedLegCount)) ? Number(p.matchedLegCount) : 0;
  const requiredLegCount = Number.isFinite(Number(p.requiredLegCount)) ? Number(p.requiredLegCount) : null;
  const recomputedProfitPct = Number.isFinite(Number(p.recomputedProfitPct)) ? Number(p.recomputedProfitPct) : null;

  const compat = _arbRawReverifyDeriveCompatibility(reason, p.rejectedRawCandidates, p.failedLegIndex);

  // Compact matched legs summary — cap at 5, no full raw payload.
  const matchedLegsSample = Array.isArray(p.matched)
    ? p.matched.slice(0, 5).map((m) => {
        if (!m || typeof m !== "object") return null;
        const r = m.raw && typeof m.raw === "object" ? m.raw : {};
        return {
          sportsbook: r.sportsbook != null ? String(r.sportsbook) : null,
          market_type: r.market_type != null ? String(r.market_type) : null,
          selection: r.selection != null ? String(r.selection) : null,
          line: r.line != null ? r.line : null,
          odds_decimal: Number.isFinite(Number(m.decimal)) ? Number(m.decimal) : null,
          rawPeriod: m.rawPeriod != null ? m.rawPeriod : null,
          lineSource: m.lineSource || null,
        };
      }).filter(Boolean)
    : [];

  // Rejected candidates are already capped at 10 upstream; expose max 5 here.
  const rejectedCandidatesSample = Array.isArray(p.rejectedRawCandidates)
    ? p.rejectedRawCandidates.slice(0, 5)
    : [];

  return {
    finalStage,
    reason,
    matchedLegCount,
    requiredLegCount,
    recomputedProfitPct,
    compatibility: compat,
    matchedLegsSample,
    rejectedCandidatesSample,
  };
}

/**
 * Reverify an Arb row from source semantics + fresh raw odds rows.
 *
 * This helper is stricter than click-time preflight and intentionally does not
 * use UI/render labels. It is used by live lifecycle reverify in server/arb.js.
 */
function reverifyArbOpportunityFromFreshRawOdds(sourceRow, rawRows, options) {
  const opts = options && typeof options === "object" ? options : {};
  const minProfitPct = Number.isFinite(Number(opts.minProfitPct)) ? Number(opts.minProfitPct) : 0;
  const nowMs = Number.isFinite(Number(opts.nowMs)) ? Number(opts.nowMs) : Date.now();
  const maxRawAgeSec = Number.isFinite(Number(opts.maxRawAgeSec)) ? Number(opts.maxRawAgeSec) : 0;

  if (!sourceRow || typeof sourceRow !== "object") {
    return {
      ok: false,
      stillArb: false,
      reason: "bad_source_row",
      recomputedProfitPct: null,
      impliedTotal: null,
      matchedLegs: [],
      updatedRowPatch: null,
      diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "source_row_validation", reason: "bad_source_row", matchedLegCount: 0, requiredLegCount: null, matched: [], rejectedRawCandidates: [] }),
    };
  }
  if (!Array.isArray(rawRows) || rawRows.length === 0) {
    return {
      ok: false,
      stillArb: false,
      reason: "raw_fetch_failed",
      recomputedProfitPct: null,
      impliedTotal: null,
      matchedLegs: [],
      updatedRowPatch: null,
      diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "raw_rows_check", reason: "raw_fetch_failed", matchedLegCount: 0, requiredLegCount: null, matched: [], rejectedRawCandidates: [] }),
    };
  }

  const eventId = _arbRawPickEventId(sourceRow);
  if (!eventId) {
    return {
      ok: false,
      stillArb: false,
      reason: "missing_event_id",
      recomputedProfitPct: null,
      impliedTotal: null,
      matchedLegs: [],
      updatedRowPatch: null,
      diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "event_id_check", reason: "missing_event_id", matchedLegCount: 0, requiredLegCount: null, matched: [], rejectedRawCandidates: [] }),
    };
  }

  const marketType = _arbRawPickMarketType(sourceRow);
  const family = _arbRawDeriveMarketFamily(marketType);
  const period = _arbRawDerivePeriod(sourceRow, marketType);
  const explicitPeriodToken = _arbRawPickContextToken(sourceRow, [
    "period",
    "half",
    "quarter",
    "map",
    "set",
    "game",
    "gameNumber",
    "mapNumber",
    "setNumber",
  ]);
  const explicitPhaseToken = _arbRawPickContextToken(sourceRow, [
    "period_name",
    "periodName",
    "currentPeriod",
    "currentQuarter",
    "stage",
    "round",
  ]);
  const sourceTopMagnitude = _arbRawTopLevelMagnitude(sourceRow);
  const picks = Array.isArray(sourceRow.picks) && sourceRow.picks.length > 0
    ? sourceRow.picks
    : (Array.isArray(sourceRow.legs) ? sourceRow.legs : []);
  const requiredPickCount = picks.length;
  const badPickDiagnostics = _arbRawBuildBadPickCountDiagnostics(sourceRow, picks, rawRows, {
    family,
    marketType,
    eventId,
    period,
  });
  if (requiredPickCount < 2) {
    const oneLegDiagnostics = {
      ...badPickDiagnostics,
      requiredPickCount,
      sourcePickCount: requiredPickCount,
      event_id: eventId || null,
      market_type: marketType || null,
      detailReason: "bad_pick_count",
    };
    return {
      ok: false,
      stillArb: false,
      reason: "bad_pick_count",
      recomputedProfitPct: null,
      impliedTotal: null,
      matchedLegs: [],
      updatedRowPatch: null,
      badPickCountDiagnostics: oneLegDiagnostics,
      diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "pick_count_check", reason: "bad_pick_count", matchedLegCount: 0, requiredLegCount: requiredPickCount, matched: [], rejectedRawCandidates: [] }),
    };
  }
  if (!badPickDiagnostics.structureOk) {
    return {
      ok: false,
      stillArb: false,
      reason: "bad_pick_count",
      recomputedProfitPct: null,
      impliedTotal: null,
      matchedLegs: [],
      updatedRowPatch: null,
      badPickCountDiagnostics: badPickDiagnostics,
      diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "pick_count_check", reason: "bad_pick_count", matchedLegCount: 0, requiredLegCount: requiredPickCount, matched: [], rejectedRawCandidates: [] }),
    };
  }

  const sourceLegs = picks.map((leg) => {
    const lineRaw = leg && leg.line != null ? leg.line : null;
    const lineNum = _arbRawFiniteNum(lineRaw);
    return {
      book: _arbRawPickLegBook(leg) || null,
      selection: leg && leg.selection != null ? String(leg.selection) : (leg && leg.sel != null ? String(leg.sel) : null),
      line: lineRaw != null ? lineRaw : null,
      numericLine: lineNum != null ? lineNum : null,
    };
  });
  const rejectedRawCandidates = [];
  const pushRejectedRawCandidate = (raw, reason, legIndex) => {
    if (rejectedRawCandidates.length >= 10) return;
    const safeRaw = raw && typeof raw === "object" ? raw : {};
    const rawLineNum = _arbRawFiniteNum(safeRaw.line);
    rejectedRawCandidates.push({
      reason: reason != null ? String(reason) : "rejected",
      legIndex: Number.isFinite(Number(legIndex)) ? Number(legIndex) : null,
      sportsbook: safeRaw.sportsbook != null ? String(safeRaw.sportsbook) : null,
      selection: safeRaw.selection != null ? String(safeRaw.selection) : null,
      line: safeRaw.line != null ? safeRaw.line : null,
      numericLine: rawLineNum != null ? rawLineNum : null,
      period: _arbRawDerivePeriod(safeRaw, safeRaw.market_type || safeRaw.market || null),
      market_type: safeRaw.market_type != null ? String(safeRaw.market_type) : (safeRaw.market != null ? String(safeRaw.market) : null),
      event_id: _arbRawPickEventId(safeRaw) || null,
    });
  };

  const matched = [];
  for (let i = 0; i < picks.length; i += 1) {
    const leg = picks[i];
    const wantBook = _arbRawNormBook(_arbRawPickLegBook(leg));
    if (!wantBook) {
      return {
        ok: false,
        stillArb: false,
        reason: "book_not_found",
        recomputedProfitPct: null,
        impliedTotal: null,
        matchedLegs: matched.slice(),
        updatedRowPatch: null,
        diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "book_lookup", reason: "book_not_found", matchedLegCount: matched.length, requiredLegCount: picks.length, matched, rejectedRawCandidates, failedLegIndex: i }),
      };
    }

    const legCandidates = [];
    const legCandidatePeriods = new Set();
    const legCandidateLines = new Set();
    const sourceLegLine = _arbRawFiniteNum(leg && leg.line);
    for (let j = 0; j < rawRows.length; j += 1) {
      const raw = rawRows[j];
      if (!raw || typeof raw !== "object") continue;
      if (_arbRawNormBook(raw.sportsbook) !== wantBook) {
        pushRejectedRawCandidate(raw, "book_mismatch", i);
        continue;
      }

      const rawEventId = _arbRawPickEventId(raw);
      if (rawEventId && String(rawEventId) !== String(eventId)) {
        pushRejectedRawCandidate(raw, "event_mismatch", i);
        continue;
      }

      const rawMt = String(raw.market_type != null ? raw.market_type : (raw.market != null ? raw.market : "")).trim();
      if (!_arbRawMarketsCompatible(marketType, rawMt)) {
        pushRejectedRawCandidate(raw, "market_mismatch", i);
        continue;
      }

      const rawPeriod = _arbRawDerivePeriod(raw, rawMt);
      legCandidatePeriods.add(rawPeriod);
      if (rawPeriod !== period) {
        pushRejectedRawCandidate(raw, "period_mismatch", i);
        continue;
      }

      if (!_arbRawSelectionMatches(sourceRow, leg, raw, family)) {
        pushRejectedRawCandidate(raw, "selection_mismatch", i);
        continue;
      }

      const dec = Number(raw.odds_decimal);
      if (!Number.isFinite(dec) || dec <= 1) {
        pushRejectedRawCandidate(raw, "bad_decimal_odds", i);
        continue;
      }

      const ts = _arbRawTimestampMs(raw);
      if (maxRawAgeSec > 0) {
        if (ts == null) {
          pushRejectedRawCandidate(raw, "missing_timestamp", i);
          continue;
        }
        const ageSec = Math.max(0, (nowMs - ts) / 1000);
        if (ageSec > maxRawAgeSec) {
          pushRejectedRawCandidate(raw, "stale_raw", i);
          continue;
        }
      }

      const rawLineNum = _arbRawFiniteNum(raw.line);
      legCandidateLines.add(
        Number.isFinite(Number(rawLineNum))
          ? Number(rawLineNum)
          : (raw.line != null ? raw.line : null)
      );
      let lineSource = "raw";
      let lineCompatible = true;
      if (family === "handicap" || family === "corner_handicap") {
        const legSigned = _arbRawSignedLineFromLeg(leg);
        if (rawLineNum == null) {
          lineCompatible = false;
        } else if (legSigned != null) {
          if (!_arbRawAbsEq(rawLineNum, legSigned)) {
            lineCompatible = false;
          }
          lineSource = "leg_signed";
        }
      } else if (family === "total" || family === "team_total") {
        if (rawLineNum == null) {
          lineCompatible = false;
        } else {
          const legUnsigned = _arbRawUnsignedLineFromLeg(leg);
          const sourceLineMag = legUnsigned != null ? legUnsigned : sourceTopMagnitude;
          if (sourceLineMag != null && !_arbRawAbsEq(Math.abs(rawLineNum), sourceLineMag)) lineCompatible = false;
          lineSource = legUnsigned != null ? "leg_unsigned" : "source_top_level";
        }
      }
      if (!lineCompatible) {
        pushRejectedRawCandidate(raw, "line_mismatch", i);
        continue;
      }

      // Conservative phase compatibility when source carries explicit phase/map/set.
      if (explicitPeriodToken || explicitPhaseToken) {
        const rawPeriodToken = _arbRawPickContextToken(raw, [
          "period",
          "half",
          "quarter",
          "map",
          "set",
          "game",
          "gameNumber",
          "mapNumber",
          "setNumber",
        ]);
        const rawPhaseToken = _arbRawPickContextToken(raw, [
          "period_name",
          "periodName",
          "currentPeriod",
          "currentQuarter",
          "stage",
          "round",
        ]);
        if (explicitPeriodToken && rawPeriodToken && explicitPeriodToken !== rawPeriodToken) {
          pushRejectedRawCandidate(raw, "explicit_period_token_mismatch", i);
          continue;
        }
        if (explicitPhaseToken && rawPhaseToken && explicitPhaseToken !== rawPhaseToken) {
          pushRejectedRawCandidate(raw, "explicit_phase_token_mismatch", i);
          continue;
        }
      }

      legCandidates.push({
        raw,
        decimal: dec,
        timestampMs: ts,
        rawPeriod,
        rawLine: rawLineNum,
        lineSource,
      });
    }

    if (legCandidates.length === 0) {
      return {
        ok: false,
        stillArb: false,
        reason: "no_compatible_raw_legs",
        recomputedProfitPct: null,
        impliedTotal: null,
        matchedLegs: matched.slice(),
        updatedRowPatch: null,
        detailDiagnostics: {
          sourcePickCount: picks.length,
          rawCandidateCount: rawRows.length,
          matchedLegCount: matched.length,
          requiredPickCount: picks.length,
          event_id: eventId || null,
          market_type: marketType || null,
          sourcePeriod: period != null ? period : null,
          candidatePeriods: Array.from(legCandidatePeriods),
          sourceLine: sourceRow.line != null ? sourceRow.line : null,
          candidateLines: Array.from(legCandidateLines),
          sourceLegs,
          rejectedRawCandidates,
          failedLegIndex: i,
          failedLeg: sourceLegs[i] || null,
          failedLegSourceLine: sourceLegLine != null ? sourceLegLine : null,
        },
        diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "leg_candidate_filter", reason: "no_compatible_raw_legs", matchedLegCount: matched.length, requiredLegCount: picks.length, matched, rejectedRawCandidates, failedLegIndex: i }),
      };
    }

    const uniquePeriods = new Set(legCandidates.map((c) => String(c.rawPeriod || "")));
    const uniqueLines = new Set(
      legCandidates
        .map((c) => (Number.isFinite(Number(c.rawLine)) ? String(Number(c.rawLine)) : "null"))
    );
    if (uniquePeriods.size > 1 || uniqueLines.size > 1) {
      return {
        ok: false,
        stillArb: false,
        reason: "ambiguous_market_period_line",
        recomputedProfitPct: null,
        impliedTotal: null,
        matchedLegs: matched.slice(),
        updatedRowPatch: null,
        detailDiagnostics: {
          sourcePickCount: picks.length,
          rawCandidateCount: rawRows.length,
          matchedLegCount: matched.length,
          requiredPickCount: picks.length,
          event_id: eventId || null,
          market_type: marketType || null,
          sourcePeriod: period != null ? period : null,
          candidatePeriods: Array.from(uniquePeriods),
          sourceLine: sourceRow.line != null ? sourceRow.line : null,
          candidateLines: Array.from(uniqueLines),
          sourceLegs,
          rejectedRawCandidates,
          failedLegIndex: i,
          failedLeg: sourceLegs[i] || null,
        },
        diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "leg_period_line_ambiguity", reason: "ambiguous_market_period_line", matchedLegCount: matched.length, requiredLegCount: picks.length, matched, rejectedRawCandidates, failedLegIndex: i }),
      };
    }

    legCandidates.sort((a, b) => {
      const ta = Number.isFinite(Number(a.timestampMs)) ? Number(a.timestampMs) : 0;
      const tb = Number.isFinite(Number(b.timestampMs)) ? Number(b.timestampMs) : 0;
      return tb - ta;
    });
    matched.push(legCandidates[0]);
  }

  const decimals = matched.map((m) => m.decimal);
  const arb = _arbRawComputeProfit(decimals);
  if (!arb || !Number.isFinite(Number(arb.invSum))) {
    return {
      ok: false,
      stillArb: false,
      reason: "unprofitable_after_raw",
      recomputedProfitPct: null,
      impliedTotal: arb && Number.isFinite(Number(arb.invSum)) ? Number(arb.invSum) : null,
      matchedLegs: matched.map((m) =>
        _arbRawDescribeMatchedForReverify(m.raw, m.decimal, m.timestampMs, m.lineSource)
      ),
      updatedRowPatch: null,
      diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "profit_compute", reason: "unprofitable_after_raw", matchedLegCount: matched.length, requiredLegCount: picks.length, matched, rejectedRawCandidates, recomputedProfitPct: null }),
    };
  }

  const recomputedProfitPct = Number(arb.profitPct);
  if (!Number.isFinite(recomputedProfitPct) || recomputedProfitPct <= minProfitPct) {
    return {
      ok: false,
      stillArb: false,
      reason: "profit_below_min",
      recomputedProfitPct: Number.isFinite(recomputedProfitPct) ? recomputedProfitPct : null,
      impliedTotal: Number(arb.invSum),
      matchedLegs: matched.map((m) =>
        _arbRawDescribeMatchedForReverify(m.raw, m.decimal, m.timestampMs, m.lineSource)
      ),
      updatedRowPatch: null,
      diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "profit_min_check", reason: "profit_below_min", matchedLegCount: matched.length, requiredLegCount: picks.length, matched, rejectedRawCandidates, recomputedProfitPct: Number.isFinite(recomputedProfitPct) ? recomputedProfitPct : null }),
    };
  }

  const picksPatch = matched.map((m) => {
    const r = m.raw || {};
    const patch = {
      odds: m.decimal,
      odds_decimal: r.odds_decimal != null ? r.odds_decimal : m.decimal,
      rawVerified: true,
      rawLastSeenAt: r.last_seen_at != null ? r.last_seen_at : (m.timestampMs != null ? m.timestampMs : nowMs),
    };
    if (r.odds_american != null) patch.odds_american = r.odds_american;
    if (r.selection != null) {
      patch.selection = String(r.selection);
      patch.sel = String(r.selection);
    }
    if (r.selection_type != null) patch.selection_type = r.selection_type;
    if (r.line != null) {
      patch.line = r.line;
      patch.verifiedLine = String(r.line);
    }
    return patch;
  });

  const topLine = matched.length > 0 && matched[0] && matched[0].raw ? matched[0].raw.line : null;
  const updatedRowPatch = {
    picks: picksPatch,
    line: topLine != null ? topLine : sourceRow.line,
    verifiedLine: topLine != null ? String(topLine) : (sourceRow.verifiedLine != null ? sourceRow.verifiedLine : null),
    profitPct: recomputedProfitPct,
    profit_percent: recomputedProfitPct,
    profitPercent: recomputedProfitPct,
    implied_total: Number(arb.invSum),
    impliedTotal: Number(arb.invSum),
    rawRecomputedProfitPct: recomputedProfitPct,
    rawVerification: {
      status: "verified",
      recomputedProfitPct,
      impliedTotal: Number(arb.invSum),
      matchedLegs: matched.map((m) =>
        _arbRawDescribeMatchedForReverify(m.raw, m.decimal, m.timestampMs, m.lineSource)
      ),
      verifiedAt: nowMs,
    },
  };

  return {
    ok: true,
    stillArb: true,
    reason: "raw_reverified",
    recomputedProfitPct,
    impliedTotal: Number(arb.invSum),
    matchedLegs: matched.map((m) =>
      _arbRawDescribeMatchedForReverify(m.raw, m.decimal, m.timestampMs, m.lineSource)
    ),
    updatedRowPatch,
    diagnostics: _arbRawReverifyBuildDiagnostics({ finalStage: "raw_reverified", reason: "raw_reverified", matchedLegCount: matched.length, requiredLegCount: picks.length, matched, rejectedRawCandidates, recomputedProfitPct }),
  };
}

function _arbRawDescribeMatched(m) {
  if (!m || typeof m !== "object") return null;
  const r = m.raw && typeof m.raw === "object" ? m.raw : {};
  return {
    sportsbook: r.sportsbook != null ? String(r.sportsbook) : null,
    market_type: r.market_type != null ? String(r.market_type) : null,
    selection: r.selection != null ? String(r.selection) : null,
    selection_type: r.selection_type != null ? String(r.selection_type) : null,
    line: r.line != null ? r.line : null,
    odds_decimal: m.decimal != null ? m.decimal : (r.odds_decimal != null ? r.odds_decimal : null),
    odds_american: r.odds_american != null ? r.odds_american : null,
    last_seen_at: r.last_seen_at != null ? r.last_seen_at : null,
    timestampMs: m.timestampMs != null ? m.timestampMs : null,
    lineSource: m.lineGate && m.lineGate.source ? m.lineGate.source : null,
    matchedMagnitude: m.lineGate && m.lineGate.magnitude != null ? m.lineGate.magnitude : null,
    matchedSigned: m.lineGate && m.lineGate.signed != null ? m.lineGate.signed : null,
  };
}

/**
 * Verify an entire batch of source rows against a Map(eventId -> rawOddsRows[]).
 *
 * @returns {{
 *   verifiedRows: object[],
 *   rejectedRows: object[],
 *   counts: {input:number, verified:number, rejected:number},
 *   rejectReasons: Record<string, number>,
 *   debugRows: object[]
 * }}
 */
function verifyArbRowsAgainstRawOdds(rows, rawOddsByEvent, options) {
  const opts = options && typeof options === "object" ? options : {};
  const list = Array.isArray(rows) ? rows : [];
  const verifiedRows = [];
  const rejectedRows = [];
  const debugRows = [];
  const rejectReasons = Object.create(null);
  let rawFetchFailed = 0;

  const map =
    rawOddsByEvent instanceof Map
      ? rawOddsByEvent
      : (() => {
          const m = new Map();
          if (rawOddsByEvent && typeof rawOddsByEvent === "object") {
            for (const k of Object.keys(rawOddsByEvent)) m.set(k, rawOddsByEvent[k]);
          }
          return m;
        })();

  for (let i = 0; i < list.length; i += 1) {
    const item = list[i];
    if (!item || typeof item !== "object") continue;
    const eid = _arbRawPickEventId(item);
    const rawRows = eid ? map.get(eid) : undefined;
    if (rawRows === undefined) {
      // We never attempted (or fetch failed for this event).
      rawFetchFailed += 1;
      const fakeResult = {
        ok: false,
        status: "rejected",
        reason: "raw_fetch_failed",
        item,
        matchedLegs: [],
        failedLegs: [],
        diagnostics: { eventId: eid || null, scope: opts.scope || null, missing: "raw_rows" },
      };
      rejectedRows.push(item);
      rejectReasons.raw_fetch_failed = (rejectReasons.raw_fetch_failed || 0) + 1;
      debugRows.push(getArbRawVerifyDebugRow(item, fakeResult));
      continue;
    }
    const result = verifySingleArbOpportunityWithRawOdds(item, rawRows, opts);
    if (result.ok && result.item) {
      verifiedRows.push(result.item);
    } else {
      rejectedRows.push(item);
      const why = String(result.reason || "rejected");
      rejectReasons[why] = (rejectReasons[why] || 0) + 1;
    }
    debugRows.push(getArbRawVerifyDebugRow(item, result));
  }

  return {
    verifiedRows,
    rejectedRows,
    counts: {
      input: list.length,
      verified: verifiedRows.length,
      rejected: rejectedRows.length,
      rawFetchFailed,
    },
    rejectReasons,
    debugRows,
  };
}

/**
 * Compact debug-safe debug row for /api/debug/arb-raw-verify.
 */
function getArbRawVerifyDebugRow(item, result) {
  const safeItem = item && typeof item === "object" ? item : {};
  const picks = Array.isArray(safeItem.picks) ? safeItem.picks : [];
  const matched = result && Array.isArray(result.matchedLegs) ? result.matchedLegs : [];
  const failed = result && Array.isArray(result.failedLegs) ? result.failedLegs : [];

  const failedByBook = new Map();
  for (const f of failed) {
    if (!f) continue;
    const k = String(f.book || "").toLowerCase();
    failedByBook.set(k, f);
  }

  const picksOut = picks.map((p, idx) => {
    const m = matched[idx];
    const f = failedByBook.get(String(_arbRawPickLegBook(p) || "").toLowerCase());
    const sourceOdds = (() => {
      const c = p && (p.odds_decimal != null ? p.odds_decimal : p.odds);
      const n = Number(c);
      return Number.isFinite(n) ? n : null;
    })();
    const sourceLine = p && (p.line != null ? String(p.line) : null);
    return {
      sportsbook: _arbRawPickLegBook(p) || null,
      selection: p && p.selection != null ? String(p.selection) : null,
      sourceLine,
      matchedRawLine: m && m.line != null ? String(m.line) : null,
      sourceOddsDecimal: sourceOdds,
      matchedRawOddsDecimal: m && m.odds_decimal != null ? Number(m.odds_decimal) : null,
      rawLastSeenAt: m && m.last_seen_at != null ? m.last_seen_at : null,
      lineSource: m && m.lineSource ? m.lineSource : null,
      reason: f && f.reason ? f.reason : null,
    };
  });

  const sourceLine = safeItem.line != null ? String(safeItem.line) : null;
  const recomputedProfitPct =
    result && Number.isFinite(Number(result.profitPct)) ? Number(result.profitPct) : null;

  return {
    id: safeItem.id != null ? String(safeItem.id) : null,
    event_id: _arbRawPickEventId(safeItem) || null,
    market_type: _arbRawPickMarketType(safeItem) || null,
    sourceLine,
    rawVerificationStatus:
      result && result.ok === true
        ? "raw_verified"
        : result && result.reason
          ? "rejected"
          : "skipped",
    recomputedProfitPct,
    reason: result && result.reason ? String(result.reason) : null,
    picks: picksOut,
    diagnostics: result && result.diagnostics ? result.diagnostics : null,
  };
}

module.exports = {
  fetchSharpArbitrageList,
  normalizeArbitrageRow,
  enrichCommercialArbitrageQuality,
  buildArbitrageBestLineGroupKey,
  arbitrageRowLooksLikeEsports: rowLooksLikeEsports,
  commercialWarningArrayLooksStale,
  // Display-only helpers (do not affect server logic).
  pickArbDisplayMarketSource,
  pickArbDisplayPeriodScope,
  pickArbDisplayLine,
  buildArbPickOddsHkDisplay,
  arbDisplayLooksLikeRawMarketKey,
  arbHumanizeRawMarketKey,
  getArbCanonicalMarketType,
  getArbCanonicalTopLevelLine,
  getArbCanonicalPeriodScope,
  getArbCanonicalMarketFamily,
  getArbCanonicalLegLineInfo,
  buildArbCanonicalMarketDisplayLabel,
  buildArbCanonicalLegDisplayLabel,
  buildArbSourceDebugSnapshot,
  // Arb leg display helpers (display layer; pure formatters).
  getArbMarketFamily,
  isArbHandicapLikeMarket,
  isArbTotalLikeMarket,
  isArbTeamTotalLikeMarket,
  pickArbLegLine,
  formatArbLegLine,
  buildArbLegDisplayLabel,
  buildArbMarketDisplayLabel,
  // Click-time preflight client-side validation helpers.
  arbPreflightServerContractOk,
  validateArbPreflightMatchedRaw,
  buildVerifiedArbCopyText,
  // Source-vs-raw mismatch diagnostics (display-only).
  mapArbPreflightFailureReasonForDisplay,
  diagnoseArbSourceVsRawMismatch,
  refineArbFailureReason,
  // Stable Arb raw-odds verification API (server.js orchestration only).
  // Future Arb matching tweaks (book/market/period/selection/line/profit)
  // belong in this file — server.js must not duplicate matching rules.
  buildArbRawVerifyFetchPlan,
  verifyArbRowsAgainstRawOdds,
  verifySingleArbOpportunityWithRawOdds,
  reverifyArbOpportunityFromFreshRawOdds,
  getArbRawVerifyDebugRow,
};
